import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useTime } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import Breadcrumbs from "../../components/private/Breadcrumbs";
import PatientSelection from "../../components/private/visits/PatientSelection";
import api from "../../api";
import {
  apiRequest,
  apiSuccess,
  apiFetchError,
  getSuccess,
} from "../../redux/api/apiActions";
import { showAlert } from "../../redux/alert/alertActions";
import { v4 as uuidv4 } from "uuid";
import {
  usePermissions,
  useUser,
  useUserRoles,
} from "../../authentication/authentication";
import { useNavigate } from "react-router-dom";
import z from "zod";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import errorChecker from "../../utils/errorChecker";
import CustomSelect from "../../components/CustomSelect";
import appointmentReasons from "../../utils/constants/visitTypes.json";
import SubmitLoading from "../../components/common/SubmitLoading";
import formatDate from "../../utils/formatDate";
import formatTime from "../../utils/formatTime";
import { FaBaby, FaFileMedical, FaUser } from "react-icons/fa";
import {
  MdBoy,
  MdGirl,
  MdMan,
  MdOutlineElderly,
  MdOutlineElderlyWoman,
} from "react-icons/md";
import { IoMan, IoWoman } from "react-icons/io5";
import { IoIosWoman } from "react-icons/io";
import {
  clearNavigationState,
  redirectWithState,
} from "../../redux/navigation/navigationActions";
import { CalendarIcon, CheckIcon, PlusIcon, UsersIcon } from "lucide-react";
import Txt from "../../components/Text";
import textTranslator from "../../utils/useTextTranslator";
import { arClass } from "../../utils/useArClass";
import TimeInput from "../../components/form/TimeInput.jsx";
import DateInput from "../../components/form/DateInput.jsx";
import { isAdminRole } from "../../utils/adminChecker.js";
import { usePageAccess } from "../../utils/usePageAccess.js";

// Translation component wrapper
const Text = ({ children, className, page = "APPOINTMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={children}>
      {children}
    </Txt>
  );
};

const createAppointmentKey = "create-appointment-" + uuidv4().split("-")[0];



const NewPatient = ({
  colors,
  onPatientSelect,
  setShowNewPatientModal,
  selectPatient,
}) => {
  usePageAccess("create-patient");
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const createPatientKey = "createPatient";
  const staffsKey = "staffs";
  const roles = useUserRoles();
  const localUser = useUser();
  const navigate = useNavigate();
  

  const [user, setUser] = useState({});
  const [staffs, setStaffs] = useState([]);
  const [gender, setGender] = useState("");
  const [civilStatus, setCivilStatus] = useState("");
  const [bloodType, setBloodType] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");


  const dispatch = useDispatch();

  // Options for selects
  const genderOptions = [
    { label: textTranslator("Male", "PATIENTS"), value: "Male" },
    { label: textTranslator("Female", "PATIENTS"), value: "Female" },
  ];
  const civilStatusOptions = [
    { label: textTranslator("Single", "PATIENTS"), value: "Single" },
    { label: textTranslator("Married", "PATIENTS"), value: "Married" },
    { label: textTranslator("Divorced", "PATIENTS"), value: "Divorced" },
    { label: textTranslator("Widowed", "PATIENTS"), value: "Widowed" },
    { label: textTranslator("Child", "PATIENTS"), value: "Child" },
  ];
  const bloodTypeOptions = [
    { label: "A+", value: "A+" },
    { label: "A-", value: "A-" },
    { label: "B+", value: "B+" },
    { label: "B-", value: "B-" },
    { label: "AB+", value: "AB+" },
    { label: "AB-", value: "AB-" },
    { label: "O+", value: "O+" },
    { label: "O-", value: "O-" },
  ];

  // theme-aware classes
  const inputBase = darkMode
    ? "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm bg-gray-700 border border-gray-600 text-white"
    : "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm border border-gray-200";
  const textareaBase = darkMode
    ? "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm bg-gray-700 border border-gray-600 text-white"
    : "rounded px-3 max-sm:text-xs py-3 max-sm:p-2 text-sm border border-gray-200";
  const labelClass = darkMode
    ? "text-xs text-gray-300"
    : "text-xs text-slate-600";
  const formCardClass = darkMode
    ? "p-4 rounded-xl border space-y-4 normal-form flex-1 bg-gray-750 border-gray-600 text-gray-100"
    : "p-4 rounded-xl border space-y-4 normal-form flex-1 bg-white border-gray-200 text-gray-900";
  const rowClass = darkMode
    ? "row flex flex-col gap-3 rounded-md p-3 bg-gray-800 text-gray-100"
    : "row flex flex-col gap-3 rounded-md p-3 bg-white";

  // zod schema
  const patientSchema = z.object({
    doctor: z.string().optional(),
    // --- REQUIRED PERSONAL INFO ---
    firstName: z
      .string()
      .min(1, textTranslator("firstNameRequired", "PATIENTS")),
    lastName: z.string().min(1, textTranslator("lastNameRequired", "PATIENTS")),
    // --- PHONE ---
    phoneCountry: z
      .string()
      .min(1, textTranslator("countryCodeRequired", "PATIENTS")),
    phone: z.string().optional(),
    // --- EMAIL ---
    email: z
      .string()
      .email(textTranslator("invalidEmail", "PATIENTS"))
      .or(z.literal("")),
    // --- CIN ---
    cin: z.string().optional(),
    // --- OTHERS (optional) ---
    birthday: z.string().optional(),
    address: z.string().optional(),
    medicalInsurance: z.string().optional(),
    profileImage: z.string().optional(),
    allergies: z.string().optional(),
    chronicConditions: z.string().optional(),
    currentMedications: z.string().optional(),
    familyMedicalHistory: z.string().optional(),
    notes: z.string().optional(),
    emergencyContact: z
      .object({
        name: z.string().optional(),
        relation: z.string().optional(),
        phone: z.string().optional(),
      })
      .optional(),
  });

  const defaultValues = {
    doctor: user.id || "",
    firstName: "",
    lastName: "",
    phoneCountry: "+212",
    phone: "",
    email: "",
    cin: "",
    birthday: "",
    address: "",
    medicalInsurance: "",
    profileImage: "",
    allergies: "",
    chronicConditions: "",
    currentMedications: "",
    familyMedicalHistory: "",
    notes: "",
    emergencyContact: { name: "", relation: "", phone: "" },
  };

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(patientSchema),
    mode: "onBlur",
    defaultValues,
  });

  // when selectPatient changes, prefill the form
  useEffect(() => {
    if (selectPatient) {
      reset({
        doctor: selectPatient.doctor || String(user.id || ""),
        firstName: selectPatient.firstName || "",
        lastName: selectPatient.lastName || "",
        phoneCountry:
          selectPatient.phone && selectPatient.phone.startsWith("+")
            ? (selectPatient.phone.match(/^\+[\d]{1,4}/) || ["+212"])[0]
            : defaultValues.phoneCountry,
        phone:
          selectPatient.phone && selectPatient.phone.startsWith("+")
            ? selectPatient.phone.replace(/^\+[\d]{1,4}/, "")
            : selectPatient.phone || "",
        email: selectPatient.email || "",
        cin: selectPatient.cin || "",
        birthday: selectPatient.birthday || "",
        address: selectPatient.address || "",
        medicalInsurance: selectPatient.medicalInsurance || "",
        profileImage: selectPatient.profileImage || "",
        allergies: selectPatient.allergies || "",
        chronicConditions: selectPatient.chronicConditions || "",
        currentMedications: selectPatient.currentMedications || "",
        familyMedicalHistory: selectPatient.familyMedicalHistory || "",
        notes: selectPatient.notes || "",
        emergencyContact: selectPatient.emergencyContact || {
          name: "",
          relation: "",
          phone: "",
        },
      });
      setGender(selectPatient.gender || "");
      setCivilStatus(selectPatient.civilStatus || "");
      setBloodType(selectPatient.bloodType || "");
      setDateOfBirth(selectPatient.birthday || "");
    } else {
      reset({ ...defaultValues, doctor: String(user.id || "") });
      setGender("");
      setCivilStatus("");
      setBloodType("");
      setDateOfBirth("");
    }
  }, [selectPatient, user, reset]);

  // fetch logged user info
  useEffect(() => {
    dispatch(apiRequest("GET", staffsKey));
    api
      .get(`users/${localUser.id}`)
      .then((res) => {
        const userData = res.data;
        setUser(userData);
        reset((prev) => ({ ...prev, doctor: String(userData.id || user.id) }));
        dispatch(
          apiSuccess(
            "GET",
            staffsKey,
            textTranslator("Fetched staffs successfully", "PATIENTS")
          )
        );
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.error ||
          textTranslator("Failed to fetch user data", "PATIENTS");
        const type = error.response?.data?.type || "error";
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  }, []);

  // fetch staffs for doctor
  useEffect(() => {
    if (localUser.role === "doctor") {
      dispatch(apiRequest("GET"));
      api
        .get(`users/staffs/by-doctor/${localUser.id}`)
        .then((res) => {
          setStaffs(res.data.results);
          dispatch(getSuccess());
        })
        .catch((error) => {
          const errorMessage =
            error.response?.data?.error ||
            textTranslator("Failed to fetch staffs", "PATIENTS");
          const type = error.response?.data?.type || "error";
          errorChecker(errorMessage, type, dispatch, errorMessage);
        });
    }
  }, []);

 

  // compute age from birthday
  const birthdayValue = watch("birthday");
  useEffect(() => {
    setDateOfBirth(birthdayValue || "");
  }, [birthdayValue]);

  const computeAge = (dateString) => {
    if (!dateString) return "";
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : "";
  };
  const age = computeAge(dateOfBirth);

  const onSubmit = (data) => {
    const payload = {
      ...data,
      phone: `${data.phoneCountry || ""}${data.phone}`,
      doctor: user.id || data.doctor,
      birthday: dateOfBirth || new Date(),
      gender: gender || "",
      civilStatus: civilStatus || "",
      bloodType: bloodType || "",
    };

    const createdPatient = {
      id: uuidv4(),
      firstName: payload.firstName,
      lastName: payload.lastName,
      phone: payload.phone,
      email: payload.email,
      birthday: payload.birthday,
      gender: payload.gender,
      address: payload.address,
      cin: payload.cin,
      civilStatus: payload.civilStatus,
      bloodType: payload.bloodType,
      allergies: payload.allergies,
      chronicConditions: payload.chronicConditions,
      currentMedications: payload.currentMedications,
      familyMedicalHistory: payload.familyMedicalHistory,
      medicalInsurance: payload.medicalInsurance,
      notes: payload.notes,
      emergencyContact: payload.emergencyContact,
      doctor: payload.doctor,
      _raw: payload,
    };

    if (typeof onPatientSelect === "function") {
      onPatientSelect(createdPatient);
    }

    if (typeof setShowNewPatientModal === "function") {
      setShowNewPatientModal(false);
    }

    reset({ ...defaultValues, doctor: String(user.id || "") });
    setGender("");
    setCivilStatus("");
    setBloodType("");
    setDateOfBirth("");
  };

  return (
    <motion.div
      className="flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className={`w-full max-w-8/12 rounded-xl ${colors.card}`}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`rounded-xl normal-form space-y-4 flex-1 w-full max-sm:p-1
            ${darkMode ? "border-gray-600" : "border-gray-200"}`}
        >
          <div className="space-y-4">
            {/* First Name & Last Name */}
            <div
              className={arClass(
                "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col"
              )}
            >
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className={labelClass}>
                  <Text page="PATIENTS">First Name</Text>
                </label>
                <input
                  {...register("firstName")}
                  className={arClass(inputBase)}
                  name="firstName"
                  placeholder={textTranslator("enterFirstName", "PATIENTS")}
                />
              </div>
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className={labelClass}>
                  <Text page="PATIENTS">Last Name</Text>
                </label>
                <input
                  {...register("lastName")}
                  className={arClass(inputBase)}
                  name="lastName"
                  placeholder={textTranslator("enterLastName", "PATIENTS")}
                />
              </div>
              {(formErrors.firstName || formErrors.lastName) && (
                <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-2">
                  {formErrors.firstName?.message && (
                    <span className={arClass("text-xs !text-red-400")}>
                      *{formErrors.firstName.message}
                    </span>
                  )}
                  {formErrors.lastName?.message && (
                    <span className={arClass("text-xs !text-red-400")}>
                      *{formErrors.lastName.message}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Birthday & Age */}
            <div
              className={arClass(
                "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col"
              )}
            >
              <div className={arClass("flex flex-col gap-2")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">Birthday</Text>
                </label>
                <DateInput
                  className={arClass(inputBase)}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  value={dateOfBirth}
                  name="birthday"
                />
              </div>
              <div className={arClass("flex flex-col gap-2 relative group")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">Age</Text>
                </label>
                <input
                  className={`${arClass(inputBase)} !cursor-not-allowed`}
                  name="age"
                  value={age || ""}
                  readOnly
                  disabled
                />
                <AnimatePresence>
                  <motion.span
                    className={`absolute left-0 top-full mt-1 hidden group-hover:block text-xs px-2 py-1 rounded z-[10000] ${
                      darkMode
                        ? "text-white bg-slate-700"
                        : "text-white bg-slate-700"
                    }`}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    exit={{ opacity: 0, y: -5 }}
                  >
                    <Text page="PATIENTS">Age is calculated based on the date of birth provided</Text>
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>

            {/* Gender & CIN */}
            <div
              className={arClass(
                "grid grid-cols-2 gap-2 max-sm:flex max-sm:flex-col items-center"
              )}
            >
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">CIN</Text>
                </label>
                <input
                  {...register("cin")}
                  className={arClass(inputBase)}
                  name="cin"
                  placeholder={textTranslator("enterCIN", "PATIENTS")}
                />
              </div>
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">Gender</Text>
                </label>
                <CustomSelect
                  options={genderOptions}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className={arClass("w-full")}
                  isMobile={window.innerWidth < 640}
                  allowCreate={false}
                  allowSearch={false}
                />
              </div>
              {(gender === "" || formErrors.cin) && (
                <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-3">
                  {gender === "" && (
                    <span className={arClass("text-xs !text-red-400")}>
                      *<Text page="PATIENTS">genderRequired</Text>
                    </span>
                  )}
                  {formErrors.cin?.message && (
                    <span className={arClass("text-xs !text-red-400")}>
                      *{formErrors.cin.message}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Phone */}
            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Phone</Text>
              </label>
              <div className="flex gap-2">
                <input
                  {...register("phone")}
                  className={arClass(`${inputBase} flex-1`)}
                  name="phone"
                  placeholder={textTranslator("enterPhoneNumber", "PATIENTS")}
                />
              </div>
              {( formErrors.phone) && (
                <div className="text-red-600 text-xs mt-1 flex flex-col gap-1">
                  {formErrors.phone?.message && (
                    <span>*{formErrors.phone.message}</span>
                  )}
                </div>
              )}
            </div>

            {/* Email */}
            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Email</Text>
              </label>
              <input
                {...register("email")}
                className={arClass(inputBase)}
                name="email"
                placeholder={textTranslator("enterEmailAddress", "PATIENTS")}
              />
              {formErrors.email && (
                <span className="text-red-600 text-xs">
                  *{formErrors.email.message}
                </span>
              )}
            </div>

            {/* Address */}
            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Address</Text>
              </label>
              <textarea
                {...register("address")}
                className={arClass(inputBase)}
                name="address"
                placeholder={textTranslator("enterHomeAddress", "PATIENTS")}
                rows={3}
              />
            </div>

            {/* Civil Status & Blood Type */}
            <div
              className={arClass(
                "grid grid-cols-2 gap-3 max-sm:flex max-sm:flex-col"
              )}
            >
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">Civil Status</Text>
                </label>
                <CustomSelect
                  options={civilStatusOptions}
                  value={civilStatus}
                  onChange={(e) => setCivilStatus(e.target.value)}
                  className={arClass("w-full")}
                  isMobile={window.innerWidth < 640}
                  allowCreate={false}
                  allowSearch={false}
                />
              </div>
              <div className={arClass("flex flex-col w-full gap-2")}>
                <label className="text-xs text-slate-600">
                  <Text page="PATIENTS">Blood Type</Text>
                </label>
                <CustomSelect
                  options={bloodTypeOptions}
                  value={bloodType}
                  onChange={(e) => setBloodType(e.target.value)}
                  className={arClass("w-full")}
                  isMobile={window.innerWidth < 640}
                  allowCreate={false}
                  allowSearch={false}
                />
              </div>
              {civilStatus === "" && (
                <div className="text-red-600 text-xs my-1 flex flex-col gap-2 col-span-3">
                  <span className={arClass("text-xs !text-red-400")}>
                    *<Text page="PATIENTS">civilStatusRequired</Text>
                  </span>
                </div>
              )}
            </div>

            {/* Medical Information */}
            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Allergies</Text>
              </label>
              <input
                {...register("allergies")}
                className={arClass(inputBase)}
                name="allergies"
                placeholder={textTranslator("enterAllergies", "PATIENTS")}
              />
            </div>

            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Chronic Conditions (comma separated)</Text>
              </label>
              <input
                {...register("chronicConditions")}
                className={arClass(inputBase)}
                name="chronicConditions"
                placeholder={textTranslator(
                  "enterChronicConditions",
                  "PATIENTS"
                )}
              />
            </div>

            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Current Medications</Text>
              </label>
              <input
                {...register("currentMedications")}
                className={arClass(inputBase)}
                name="currentMedications"
                placeholder={textTranslator(
                  "enterCurrentMedications",
                  "PATIENTS"
                )}
              />
            </div>

            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Family Medical History</Text>
              </label>
              <input
                {...register("familyMedicalHistory")}
                className={arClass(inputBase)}
                name="familyMedicalHistory"
                placeholder={textTranslator(
                  "Enter family medical history",
                  "PATIENTS"
                )}
              />
            </div>

            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Medical Insurance</Text>
              </label>
              <input
                {...register("medicalInsurance")}
                className={arClass(inputBase)}
                name="medicalInsurance"
                placeholder={textTranslator(
                  "enterInsuranceProvider",
                  "PATIENTS"
                )}
              />
            </div>

            <div className={arClass("flex flex-col gap-2")}>
              <label className={labelClass}>
                <Text page="PATIENTS">Notes</Text>
              </label>
              <textarea
                {...register("notes")}
                className={arClass(textareaBase)}
                name="notes"
                placeholder={textTranslator("enterNotes", "PATIENTS")}
                rows={3}
              />
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="pt-4 border-t border-gray-200">
            <h4 className="text-sm font-semibold mb-3">
              <Text page="PATIENTS">Emergency Contact</Text>
            </h4>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex flex-col gap-2">
                <label className={labelClass}>
                  <Text page="PATIENTS">Name</Text>
                </label>
                <input
                  {...register("emergencyContact.name")}
                  className={arClass(inputBase)}
                  name="emergencyContact.name"
                  placeholder={textTranslator(
                    "enterEmergencyContactName",
                    "PATIENTS"
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>
                  <Text page="PATIENTS">Relation</Text>
                </label>
                <input
                  {...register("emergencyContact.relation")}
                  className={arClass(inputBase)}
                  name="emergencyContact.relation"
                  placeholder={textTranslator(
                    "enterEmergencyContactRelationship",
                    "PATIENTS"
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className={labelClass}>
                  <Text page="PATIENTS">Phone</Text>
                </label>
                <input
                  {...register("emergencyContact.phone")}
                  className={arClass(inputBase)}
                  name="emergencyContact.phone"
                  placeholder={textTranslator("contactPhone", "PATIENTS")}
                />
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className={defaultLanguage === "ar" ? "mt-6 flex justify-start  gap-3 w-full" : "mt-6 flex justify-end  gap-3 w-full"}>
            {selectPatient && (
              <motion.button
                type="button"
                className={`px-4 py-2 rounded-lg font-medium text-sm max-sm:text-xs shadow transition-all duration-200 max-sm:rounded-md max-sm:flex-1 ${
                  darkMode
                    ? "bg-red-700 text-red-200 hover:bg-red-600"
                    : "bg-red-200 text-red-800 hover:bg-red-300"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onPatientSelect(null)}
              >
                <Text>Cancel</Text>
              </motion.button>
            )}
            <motion.button
              type="submit"
              className={`px-4 py-2 rounded-lg font-medium text-sm max-sm:text-xs shadow transition-all duration-200 max-sm:rounded-md max-sm:flex-1 ${
                darkMode
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!isValid || gender === "" || civilStatus === ""}
              title={
                !isValid || gender === "" || civilStatus === ""
                  ? textTranslator("fillFormCorrectly", "PATIENTS")
                  : textTranslator("Use Patient", "PATIENTS")
              }
            >
              <Text>Next</Text>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};



const PatinetView = ({ colors, patient }) => {
  usePageAccess("patients");
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const user = useUser();

  const borderStyle = defaultLanguage === "ar" ? "border-r-4" : "border-l-4";
  const avatarSelector = (patient) => {
    const { gender, age } = patient;

    if (gender === "Male") {
      if (age < 8) return "/images/avatars/young-boy.png";
      else if (age < 18) return "/images/avatars/boy.png";
      else if (age < 30) return "/images/avatars/adult-man.png";
      else if (age < 50) return "/images/avatars/man.png";
      else return "/images/avatars/old-man.png";
    } else {
      if (age < 8) return "/images/avatars/young-girl.png";
      else if (age < 18) return "/images/avatars/girl.png";
      else if (age < 30) return "/images/avatars/adult-woman.png";
      else if (age < 50) return "/images/avatars/woman.png";
      else return "/images/avatars/old-woman.png";
    }
  };

  const iconSelector = (patient) => {
    const { gender, age } = patient;

    if (gender === "Male") {
      if (age < 8) return <FaBaby className="text-white" size={20} />;
      else if (age < 18) return <MdBoy className="text-white" size={20} />;
      else if (age < 30) return <MdMan className="text-white" size={20} />;
      else if (age < 50) return <IoMan className="text-white" size={20} />;
      else return <MdOutlineElderly className="text-white" size={20} />;
    } else {
      if (age < 8) return <FaBaby className="text-white" size={20} />;
      else if (age < 18) return <MdGirl className="text-white" size={20} />;
      else if (age < 30) return <IoWoman className="text-white" size={20} />;
      else if (age < 50) return <IoIosWoman className="text-white" size={20} />;
      else return <MdOutlineElderlyWoman className="text-white" size={20} />;
    }
  };

  return (
    <div className="col-span-4 h-full max-sm:col-span-1">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`h-full flex flex-col rounded-2xl border-2 bg-gradient-to-br max-sm:rounded-xl max-sm:border ${
          darkMode
            ? "from-blue-900/20 to-cyan-900/20 border-blue-400/30"
            : "from-blue-50 to-cyan-50 border-blue-200"
        } shadow-xl overflow-hidden`}
      >
        {patient ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 overflow-auto"
          >
            {/* Header Section */}
            <div className="p-6 border-b border-blue-200/30 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 max-sm:p-4">
              <div
                className={arClass(
                  "flex items-start justify-between mb-4 max-sm:mb-3"
                )}
              >
                <div
                  className={arClass(
                    "flex items-start gap-4 flex-1 max-sm:gap-3"
                  )}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 10,
                      delay: 0.3,
                    }}
                    className="relative flex-shrink-0"
                  >
                    <motion.img
                      src={avatarSelector(patient)}
                      alt={`${patient.firstName} ${patient.lastName}`}
                      className={`w-20 h-20 rounded-full shadow-lg border-2 max-sm:w-16 max-sm:h-16 ${
                        darkMode ? "border-blue-400" : "border-blue-200"
                      }`}
                      whileHover={{ scale: 1.05, rotate: 2 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 10,
                      }}
                    />
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 }}
                      className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full border-2 max-sm:w-6 max-sm:h-6 ${
                        darkMode ? "border-gray-800" : "border-white"
                      } flex items-center justify-center ${
                        patient.gender === "Male"
                          ? "bg-blue-500"
                          : "bg-pink-500"
                      }`}
                    >
                      {React.cloneElement(iconSelector(patient), { size: 14 })}
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className={arClass("flex-1 min-w-0")}
                  >
                    <div
                      className={arClass(
                        "flex items-center justify-between gap-3 mb-2 max-sm:gap-2"
                      )}
                    >
                      <h3
                        className={`text-2xl font-bold ${colors.text.primary} truncate max-sm:text-sm`}
                      >
                        {patient.firstName} {patient.lastName}
                      </h3>
                      <span
                        className={`text-xs px-2 py-1 rounded-full flex-shrink-0 max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                          darkMode
                            ? "bg-blue-500/20 text-blue-300"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        <Text>Selected</Text>
                      </span>
                    </div>

                    <div
                      className={arClass(
                        "flex flex-wrap items-center gap-1 mb-4 max-sm:mb-3 max-sm:gap-0.5"
                      )}
                    >
                      <span
                        className={arClass(
                          `text-xsm font-semibold px-1 py-1 rounded-full max-sm:text-xs max-sm:px-1 ${
                            darkMode
                              ? "bg-blue-500/20 text-blue-300 border border-blue-400/30"
                              : "bg-blue-100 text-blue-600 border border-blue-200"
                          }`
                        )}
                      >
                        {formatDate(patient.createdAt, false)}
                      </span>
                      <span
                        className={arClass(
                          `text-xsm font-semibold px-3 py-1 rounded-full max-sm:text-xs max-sm:px-2 ${
                            darkMode
                              ? "bg-blue-500/20 text-blue-300 border border-blue-400/30"
                              : "bg-blue-100 text-blue-600 border border-blue-200"
                          }`
                        )}
                      >
                        {

                        }
                        <Text page="PATIENTS">{patient.creator?.role}</Text>{" "}
                        {" - "}
                        {patient.creator?.firstName}
                        {patient.creator?.lastName}
                        <small>
                          {parseInt(user.id) === parseInt(patient.creator?.id)
                            ? textTranslator(" (Me)", "APPOINTMENTS")
                            : ""}
                        </small>
                      </span>
                    </div>
                    <div
                      className={arClass(
                        "flex flex-wrap items-center gap-3 mb-4 max-sm:gap-2 max-sm:mb-3"
                      )}
                    >
                      <span
                        className={arClass(
                          `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                            darkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          }`
                        )}
                      >
                        <Text page="PATIENTS">ID</Text>: {patient.id}
                      </span>
                      <span
                        className={arClass(
                          `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                            darkMode
                              ? "bg-green-500/20 text-green-300"
                              : "bg-green-100 text-green-600"
                          }`
                        )}
                      >
                        <Text page="PATIENTS">{patient.gender}</Text>
                      </span>
                      <span
                        className={arClass(
                          `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                            darkMode
                              ? "bg-purple-500/20 text-purple-300"
                              : "bg-purple-100 text-purple-600"
                          }`
                        )}
                      >
                        <Text page="PATIENTS">Age</Text>: {patient.age}
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Quick Stats Grid */}
              <motion.div
                className="grid grid-cols-4 gap-3 max-sm:grid-cols-2 max-sm:gap-2"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div
                  className={`text-center p-3 rounded-xl border max-sm:p-2 ${
                    darkMode
                      ? "bg-blue-800/20 border-blue-700/50"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                      darkMode ? "text-blue-300" : "text-blue-600"
                    }`}
                  >
                    <Text page="PATIENTS">Age</Text>
                  </p>
                  <p
                    className={`text-2xl font-bold max-sm:text-sm ${colors.text.primary}`}
                  >
                    {patient.age}
                  </p>
                </div>

                <div
                  className={`text-center p-3 rounded-xl border max-sm:p-2 ${
                    darkMode
                      ? "bg-purple-800/20 border-purple-700/50"
                      : "bg-purple-50 border-purple-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                      darkMode ? "text-purple-300" : "text-purple-600"
                    }`}
                  >
                    <Text page="PATIENTS">Gender</Text>
                  </p>
                  <p
                    className={`text-lg font-bold max-sm:text-sm ${colors.text.primary}`}
                  >
                    <Text page="PATIENTS">{patient.gender}</Text>
                  </p>
                </div>

                <div
                  className={`text-center p-3 rounded-xl border max-sm:p-2 ${
                    darkMode
                      ? "bg-red-800/20 border-red-700/50"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                      darkMode ? "text-red-300" : "text-red-600"
                    }`}
                  >
                    <Text page="PATIENTS">Blood Type</Text>
                  </p>
                  <p
                    className={`text-lg font-bold max-sm:text-sm ${colors.text.primary}`}
                  >
                    {patient.bloodType || textTranslator("—", "APPOINTMENTS")}
                  </p>
                </div>

                <div
                  className={`text-center p-3 rounded-xl border max-sm:p-2 ${
                    darkMode
                      ? "bg-green-800/20 border-green-700/50"
                      : "bg-green-50 border-green-200"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                      darkMode ? "text-green-300" : "text-green-600"
                    }`}
                  >
                    <Text>Status</Text>
                  </p>
                  <p
                    className={`text-sm font-bold max-sm:text-xs ${colors.text.primary} leading-tight`}
                  >
                    <Text page="PATIENTS">
                      {patient.civilStatus ||
                        textTranslator("—", "APPOINTMENTS")}
                    </Text>
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Content Sections - Scrollable */}
            <div
              className={arClass(
                "p-6 space-y-6 max-sm:p-4 max-sm:space-y-4 overflow-y-auto"
              )}
            >
              {/* Personal Information */}
              <motion.section
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <h4
                  className={arClass(
                    `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                  )}
                >
                  <FaUser className="text-blue-500 max-sm:w-4 max-sm:h-4" />
                  <Text>Personal Information</Text>
                </h4>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-2">
                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`}
                    >
                      <Text>Date of Birth</Text>
                    </span>
                    <p
                      className={arClass(
                        `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                      )}
                    >
                      {formatDate(patient.birthday, false) ||
                        textTranslator("—", "APPOINTMENTS")}
                    </p>
                  </div>
                  <div>
                    <span
                      className={arClass(
                        `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`
                      )}
                    >
                      <Text>CIN</Text>
                    </span>
                    <p
                      className={arClass(
                        `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                      )}
                    >
                      {patient.cin || textTranslator("—", "APPOINTMENTS")}
                    </p>
                  </div>
                  <div className="col-span-2 max-sm:col-span-1">
                    <span
                      className={arClass(
                        `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`
                      )}
                    >
                      <Text>Address</Text>
                    </span>
                    <p
                      className={arClass(
                        `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                      )}
                    >
                      {patient.address || textTranslator("—", "APPOINTMENTS")}
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Contact Information */}
              <motion.section
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <h4
                  className={arClass(
                    `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                  )}
                >
                  <FaUser className="text-green-500 max-sm:w-4 max-sm:h-4" />
                  <Text>Contact Information</Text>
                </h4>
                <div className={arClass("space-y-3 max-sm:space-y-2")}>
                  <div
                    className={arClass(
                      "flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-green-500/5 to-emerald-500/5 max-sm:p-2"
                    )}
                  >
                    <span
                      className={arClass(
                        `text-sm max-sm:text-xs ${colors.text.muted}`
                      )}
                    >
                      <Text>Phone</Text>
                    </span>
                    <span
                      className={arClass(
                        `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                      )}
                    >
                      {patient.phone || textTranslator("—", "APPOINTMENTS")}
                    </span>
                  </div>
                  <div
                    className={arClass(
                      "flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-500/5 to-cyan-500/5 max-sm:p-2"
                    )}
                  >
                    <span
                      className={arClass(
                        `text-sm max-sm:text-xs ${colors.text.muted}`
                      )}
                    >
                      <Text>Email</Text>
                    </span>
                    <span
                      className={arClass(
                        `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                      )}
                    >
                      {patient.email || textTranslator("—", "APPOINTMENTS")}
                    </span>
                  </div>
                </div>
              </motion.section>

              {/* Medical Information - Only on larger screens */}
              <motion.section
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="max-sm:hidden"
              >
                <h4
                  className={arClass(
                    `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                  )}
                >
                  <FaFileMedical className="text-red-500 max-sm:w-4 max-sm:h-4" />
                  <Text>Medical Information</Text>
                </h4>
                <div className="space-y-4 max-sm:space-y-2">
                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`}
                    >
                      <Text>Allergies</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`p-3 rounded-xl max-sm:p-2 ${borderStyle} ${
                        patient.allergies
                          ? "bg-red-500/10 border-red-400"
                          : "bg-gray-500/10 border-gray-400"
                      }`}
                    >
                      <p
                        className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                      >
                        {patient.allergies ||
                          textTranslator("No known allergies", "APPOINTMENTS")}
                      </p>
                    </motion.div>
                  </div>

                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`}
                    >
                      <Text>Chronic Conditions</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`p-3 rounded-xl  max-sm:p-2 ${borderStyle} ${
                        patient.chronicConditions
                          ? "bg-yellow-500/10 border-yellow-400"
                          : "bg-gray-500/10 border-gray-400"
                      }`}
                    >
                      <p
                        className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                      >
                        {patient.chronicConditions ||
                          textTranslator("None reported", "APPOINTMENTS")}
                      </p>
                    </motion.div>
                  </div>

                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`}
                    >
                      <Text>Current Medications</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`p-3 rounded-xl ${borderStyle} max-sm:p-2 ${
                        patient.currentMedications
                          ? "bg-blue-500/10 border-blue-400"
                          : "bg-gray-500/10 border-gray-400"
                      }`}
                    >
                      <p
                        className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                      >
                        {patient.currentMedications ||
                          textTranslator("None", "APPOINTMENTS")}
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.section>

              {/* Additional Information - Only on larger screens */}
              <motion.section
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="max-sm:hidden"
              >
                <h4
                  className={arClass(
                    `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                  )}
                >
                  <FaFileMedical className="text-purple-500 max-sm:w-4 max-sm:h-4" />
                  <Text>Additional Information</Text>
                </h4>
                <div className={arClass("space-y-4 max-sm:space-y-2")}>
                  {/* Emergency Contact */}
                  <div>
                    <span
                      className={arClass(
                        `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`
                      )}
                    >
                      <Text>Emergency Contact</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={arClass(
                        "p-3 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-200/30 max-sm:p-2"
                      )}
                    >
                      {patient.emergencyContact ? (
                        <div className="space-y-2">
                          <p
                            className={arClass(
                              "text-sm font-semibold max-sm:text-xs " +
                                colors.text.primary
                            )}
                          >
                            {patient.emergencyContact.name}
                          </p>
                          <p
                            className={arClass(
                              "text-xs max-sm:text-xsm " + colors.text.muted
                            )}
                          >
                            <Text>Relation</Text>:{" "}
                            {patient.emergencyContact.relation}
                          </p>
                          <p
                            className={arClass(
                              "text-xs max-sm:text-xsm " + colors.text.muted
                            )}
                          >
                            <Text>Phone</Text>: {patient.emergencyContact.phone}
                          </p>
                        </div>
                      ) : (
                        <p
                          className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                        >
                          <Text>No emergency contact provided</Text>
                        </p>
                      )}
                    </motion.div>
                  </div>

                  {/* Medical Insurance */}
                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`}
                    >
                      <Text>Medical Insurance</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`p-3 rounded-xl bg-gradient-to-r from-emerald-500/5 to-teal-500/5 border border-emerald-200/30 max-sm:p-2`}
                    >
                      <p
                        className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                      >
                        {patient.medicalInsurance ||
                          textTranslator("Not specified", "APPOINTMENTS")}
                      </p>
                    </motion.div>
                  </div>

                  {/* Notes */}
                  <div>
                    <span
                      className={`text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-2`}
                    >
                      <Text>Notes</Text>
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className={`p-3 rounded-xl bg-gradient-to-r from-orange-500/5 to-amber-500/5 border border-orange-200/30 max-sm:p-2`}
                    >
                      <p
                        className={`text-sm max-sm:text-xs ${colors.text.primary}`}
                      >
                        {patient.notes ||
                          textTranslator("No additional notes", "APPOINTMENTS")}
                      </p>
                    </motion.div>
                  </div>
                </div>
              </motion.section>

              {/* Mobile-only simplified medical info */}
              <motion.section
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="sm:hidden"
              >
                <h4
                  className={`text-base font-semibold mb-3 ${colors.text.primary} flex items-center gap-2`}
                >
                  <FaFileMedical className="text-red-500 w-4 h-4" />
                  <Text>Quick Medical Info</Text>
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-red-500/5 to-pink-500/5">
                    <span className="text-xs text-gray-500">
                      <Text>Allergies</Text>
                    </span>
                    <span className="text-xs font-medium">
                      {patient.allergies
                        ? textTranslator("Yes", "APPOINTMENTS")
                        : textTranslator("None", "APPOINTMENTS")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-yellow-500/5 to-amber-500/5">
                    <span className="text-xs text-gray-500">
                      <Text>Conditions</Text>
                    </span>
                    <span className="text-xs font-medium">
                      {patient.chronicConditions
                        ? textTranslator("Yes", "APPOINTMENTS")
                        : textTranslator("None", "APPOINTMENTS")}
                    </span>
                  </div>
                </div>
              </motion.section>
            </div>
          </motion.div>
        ) : (
          // Empty Data
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 flex-1 flex flex-col justify-center items-center max-sm:p-4"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              className="mb-6 max-sm:mb-4"
            >
              <FaUser
                className={`w-20 h-20 max-sm:w-16 max-sm:h-16 ${colors.text.muted}`}
              />
            </motion.div>
            <motion.h4
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className={`text-xl font-semibold mb-3 max-sm:text-lg max-sm:mb-2 ${colors.text.primary}`}
            >
              <Text>Patient Not Found</Text>
            </motion.h4>
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={`text-sm text-center max-w-xs max-sm:text-xs ${colors.text.secondary}`}
            >
              <Text>
                Patient data could not be loaded. Please try again or select a
                different patient.
              </Text>
            </motion.p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// DatePicker and TimePicker components remain unchanged...
const PatientFromSourceSkeleton = ({ darkMode, colors }) => {
  return (
    <div className={arClass("col-span-2")}>
      <div className={arClass("relative overflow-hidden w-full")}>
        {/* Shimmer Effect */}
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "linear",
          }}
          className={arClass(
            `absolute inset-0 bg-gradient-to-r from-transparent via-${
              darkMode ? "blue-900/10" : "blue-50/30"
            } to-transparent`
          )}
        />

        {/* Main Container */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={arClass(
            `relative ${
              darkMode
                ? "bg-gradient-to-br from-gray-800 via-blue-900/10 to-gray-800 border-blue-800/30"
                : "bg-gradient-to-br from-white via-white to-blue-50/30 border-blue-200/50"
            } border rounded-xl p-4 shadow-inner max-sm:p-3`
          )}
        >
          {/* Header */}
          <div
            className={arClass(
              "flex items-start justify-between mb-3 max-sm:mb-2.5"
            )}
          >
            <div className={arClass("flex items-start flex-1 min-w-0")}>
              {/* Avatar with glow effect */}
              <div className={arClass("relative flex-shrink-0")}>
                <motion.div
                  animate={{ opacity: [0.3, 0.5, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={arClass(
                    "absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur opacity-30 max-sm:opacity-25 dark:from-blue-600 dark:to-indigo-600"
                  )}
                />
                <motion.div
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={arClass(
                    `relative w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full shadow max-sm:w-9 max-sm:h-9 ${
                      darkMode
                        ? "from-gray-700 to-gray-600"
                        : "from-blue-200 to-indigo-200"
                    }`
                  )}
                />
              </div>

              {/* Patient Info */}
              <div className={arClass("ml-3 flex-1 min-w-0 max-sm:ml-2.5")}>
                <div className={arClass("flex flex-col")}>
                  {/* Patient Name */}
                  <motion.div
                    animate={{ width: ["70%", "85%", "70%"] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={arClass(
                      `h-5 rounded-lg mb-1.5 max-sm:h-4 ${
                        darkMode
                          ? "bg-gradient-to-r from-gray-700 to-gray-600"
                          : "bg-gradient-to-r from-blue-200 to-indigo-200"
                      }`
                    )}
                  />

                  {/* Status badges */}
                  <div
                    className={arClass(
                      "flex flex-wrap items-center gap-1.5 max-sm:gap-1"
                    )}
                  >
                    <motion.div
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className={arClass(
                        `h-5 rounded-full w-24 max-sm:h-4 max-sm:w-20 ${
                          darkMode ? "bg-gray-700" : "bg-green-100"
                        }`
                      )}
                    />
                    <motion.div
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.1 }}
                      className={arClass(
                        `h-4 rounded-lg w-20 max-sm:h-3.5 max-sm:w-16 ${
                          darkMode ? "bg-gray-700" : "bg-gray-200"
                        }`
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Button */}
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className={arClass(
                `w-9 h-9 rounded-lg ml-2 max-sm:mx-1 max-sm:w-7 max-sm:h-7 ${
                  darkMode ? "bg-gray-700" : "bg-gray-100"
                }`
              )}
            />
          </div>

          {/* Patient Details Grid */}
          <div className={arClass("grid grid-cols-2 gap-2 max-sm:gap-1.5")}>
            {[1, 2].map((item, index) => (
              <motion.div
                key={item}
                animate={{ scale: [1, 1.02, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: index * 0.2,
                }}
                className={arClass(
                  `p-3 rounded-lg border max-sm:p-2 ${
                    darkMode
                      ? "bg-gray-800/60 border-gray-700"
                      : "bg-white/90 border-gray-200/70"
                  }`
                )}
              >
                <div className={arClass("flex items-center")}>
                  {/* Icon */}
                  <motion.div
                    animate={{ opacity: [0.6, 0.8, 0.6] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: index * 0.1,
                    }}
                    className={arClass(
                      `p-1.5 rounded-md mr-2 max-sm:mr-1.5 max-sm:p-1 ${
                        darkMode
                          ? index === 0
                            ? "bg-blue-900/30"
                            : "bg-pink-900/30"
                          : index === 0
                          ? "bg-blue-100"
                          : "bg-pink-100"
                      }`
                    )}
                  >
                    <div
                      className={arClass(
                        `w-3.5 h-3.5 max-sm:w-3 max-sm:h-3 ${
                          darkMode ? "bg-gray-600" : "bg-gray-300"
                        } rounded-sm`
                      )}
                    />
                  </motion.div>

                  <div className="min-w-0 flex-1">
                    {/* Label */}
                    <motion.div
                      animate={{ width: ["40%", "50%", "40%"] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.15,
                      }}
                      className={arClass(
                        `h-3 rounded-lg mb-1.5 max-sm:h-2.5 max-sm:mb-1 ${
                          darkMode ? "bg-gray-600" : "bg-gray-300"
                        }`
                      )}
                    />

                    {/* Value */}
                    <motion.div
                      animate={{ width: ["30%", "40%", "30%"] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.25,
                      }}
                      className={arClass(
                        `h-5 rounded-lg max-sm:h-4 ${
                          darkMode ? "bg-gray-700" : "bg-gray-400"
                        }`
                      )}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Optional Contact Info - Only show if would be present */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={arClass("mt-3 max-sm:mt-2")}
          >
            <div className={arClass("flex flex-wrap gap-2 max-sm:gap-1.5")}>
              {[1, 2].map((item, index) => (
                <div key={item} className={arClass("flex items-center")}>
                  <motion.div
                    animate={{ opacity: [0.5, 0.7, 0.5] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: index * 0.2,
                    }}
                    className={arClass(
                      `p-1 rounded-md mr-1.5 max-sm:p-0.5 max-sm:mr-1 ${
                        darkMode ? "bg-gray-700" : "bg-gray-100"
                      }`
                    )}
                  >
                    <div
                      className={arClass(
                        `w-3 h-3 max-sm:w-2.5 max-sm:h-2.5 ${
                          darkMode ? "bg-gray-600" : "bg-gray-400"
                        } rounded-sm`
                      )}
                    />
                  </motion.div>
                  <motion.div
                    animate={{ width: ["60px", "80px", "60px"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.1,
                    }}
                    className={arClass(
                      `h-3.5 rounded-lg max-sm:h-3 ${
                        darkMode ? "bg-gray-600" : "bg-gray-300"
                      } w-16`
                    )}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

const PatientViewSkeleton = ({ colors, darkMode }) => {
  return (
    <div className="col-span-4 h-full max-sm:col-span-1">
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`h-full flex flex-col rounded-2xl border-2 bg-gradient-to-br max-sm:rounded-xl max-sm:border ${
          darkMode
            ? "from-blue-900/20 to-cyan-900/20 border-blue-400/30"
            : "from-blue-50 to-cyan-50 border-blue-200"
        } shadow-xl overflow-hidden`}
      >
        {/* Header Section Skeleton */}
        <div className="p-6 border-b border-blue-200/30 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 max-sm:p-4">
          <div className="flex items-start justify-between mb-4 max-sm:mb-3">
            <div className="flex items-start gap-4 flex-1 max-sm:gap-3">
              {/* Avatar Skeleton */}
              <motion.div
                animate={{ scale: [0.95, 1, 0.95] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="relative flex-shrink-0"
              >
                <div
                  className={`w-20 h-20 rounded-full shadow-lg border-2 max-sm:w-16 max-sm:h-16 ${
                    darkMode ? "border-blue-400" : "border-blue-200"
                  } animate-pulse ${
                    darkMode
                      ? "bg-gray-700"
                      : "bg-gradient-to-br from-blue-200 to-indigo-200"
                  }`}
                />

                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full border-2 max-sm:w-6 max-sm:h-6 ${
                    darkMode ? "border-gray-800" : "border-white"
                  } ${darkMode ? "bg-gray-600" : "bg-blue-300"}`}
                />
              </motion.div>

              {/* Name and Info Skeleton */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-2 max-sm:gap-2">
                  <motion.div
                    animate={{ width: ["60%", "80%", "60%"] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className={`h-8 rounded-lg max-sm:h-6 ${
                      darkMode
                        ? "bg-gradient-to-r from-gray-700 to-gray-600"
                        : "bg-gradient-to-r from-blue-200 to-indigo-200"
                    } w-48 max-sm:w-36`}
                  />
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className={`h-6 rounded-full max-sm:h-5 ${
                      darkMode ? "bg-blue-500/20" : "bg-blue-100"
                    } w-20 max-sm:w-16`}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-1 mb-4 max-sm:mb-3 max-sm:gap-0.5">
                  <motion.div
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className={`h-6 rounded-full max-sm:h-5 max-sm:text-xs ${
                      darkMode ? "bg-blue-500/20" : "bg-blue-100"
                    } w-32 max-sm:w-24`}
                  />
                  <motion.div
                    animate={{ opacity: [0.6, 1, 0.6] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                    className={`h-6 rounded-full max-sm:h-5 max-sm:text-xs ${
                      darkMode ? "bg-blue-500/20" : "bg-blue-100"
                    } w-40 max-sm:w-32`}
                  />
                </div>

                {/* ID Badges Skeleton */}
                <div className="flex flex-wrap items-center gap-3 mb-4 max-sm:gap-2 max-sm:mb-3">
                  {[1, 2, 3].map((item, index) => (
                    <motion.div
                      key={item}
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: index * 0.2,
                      }}
                      className={`h-6 rounded-full max-sm:h-5 ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      } w-20 max-sm:w-16`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Grid Skeleton */}
          <motion.div
            className="grid grid-cols-4 gap-3 max-sm:grid-cols-2 max-sm:gap-2"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {[
              { color: "blue", label: textTranslator("Age", "APPOINTMENTS") },
              {
                color: "purple",
                label: textTranslator("Gender", "APPOINTMENTS"),
              },
              { color: "red", label: textTranslator("Blood", "APPOINTMENTS") },
              {
                color: "green",
                label: textTranslator("Status", "APPOINTMENTS"),
              },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                animate={{ y: [0, -3, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: index * 0.1,
                }}
                className={`text-center p-3 rounded-xl border max-sm:p-2 ${
                  darkMode
                    ? `bg-${stat.color}-800/20 border-${stat.color}-700/50`
                    : `bg-${stat.color}-50 border-${stat.color}-200`
                }`}
              >
                <motion.div
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={`h-3 rounded-lg mb-2 mx-auto max-sm:h-2.5 max-sm:mb-1 ${
                    darkMode ? "bg-gray-600" : "bg-gray-300"
                  } w-12 max-sm:w-10`}
                />
                <motion.div
                  animate={{ scale: [0.9, 1, 0.9] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                  className={`h-7 rounded-lg mx-auto max-sm:h-6 ${
                    darkMode ? "bg-gray-700" : "bg-gray-200"
                  } w-16 max-sm:w-12`}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Content Sections Skeleton */}
        <div className="p-6 space-y-6 max-sm:p-4 max-sm:space-y-4 overflow-y-auto">
          {/* Personal Information Skeleton */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2 mb-4 max-sm:mb-3">
              <div
                className={`w-5 h-5 rounded-full max-sm:w-4 max-sm:h-4 ${
                  darkMode ? "bg-blue-600" : "bg-blue-400"
                }`}
              />
              <motion.div
                animate={{ width: ["150px", "180px", "150px"] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`h-6 rounded-lg max-sm:h-5 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                } max-sm:w-32`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-2">
              {[1, 2, 3].map((item, index) => (
                <motion.div
                  key={item}
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.1,
                  }}
                  className={item === 3 ? "col-span-2 max-sm:col-span-1" : ""}
                >
                  <div
                    className={`h-3 rounded-lg mb-2 max-sm:h-2.5 max-sm:mb-1 ${
                      darkMode ? "bg-gray-600" : "bg-gray-300"
                    } w-24 max-sm:w-20`}
                  />
                  <div
                    className={`h-5 rounded-lg max-sm:h-4 ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    } ${
                      item === 3 ? "w-3/4 max-sm:w-full" : "w-32 max-sm:w-24"
                    }`}
                  />
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Contact Information Skeleton */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-2 mb-4 max-sm:mb-3">
              <div
                className={`w-5 h-5 rounded-full max-sm:w-4 max-sm:h-4 ${
                  darkMode ? "bg-green-600" : "bg-green-400"
                }`}
              />
              <motion.div
                animate={{ width: ["160px", "190px", "160px"] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`h-6 rounded-lg max-sm:h-5 ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                } max-sm:w-36`}
              />
            </div>

            <div className="space-y-3 max-sm:space-y-2">
              {[1, 2].map((item, index) => (
                <motion.div
                  key={item}
                  animate={{ x: index % 2 === 0 ? [0, 2, 0] : [0, -2, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-green-500/5 to-emerald-500/5 max-sm:p-2"
                >
                  <div
                    className={`h-4 rounded-lg max-sm:h-3 ${
                      darkMode ? "bg-gray-600" : "bg-gray-300"
                    } w-16 max-sm:w-12`}
                  />
                  <motion.div
                    animate={{ width: ["80px", "100px", "80px"] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                    className={`h-4 rounded-lg max-sm:h-3 ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    } max-sm:w-16`}
                  />
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Medical Information Skeleton - Desktop Only */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="max-sm:hidden"
          >
            <div className="flex items-center gap-2 mb-4">
              <div
                className={`w-5 h-5 rounded-full ${
                  darkMode ? "bg-red-600" : "bg-red-400"
                }`}
              />
              <motion.div
                animate={{ width: ["170px", "200px", "170px"] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`h-6 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
            </div>

            <div className="space-y-4">
              {[
                {
                  color: "red",
                  label: textTranslator("Allergies", "APPOINTMENTS"),
                },
                {
                  color: "yellow",
                  label: textTranslator("Chronic Conditions", "APPOINTMENTS"),
                },
                {
                  color: "blue",
                  label: textTranslator("Current Medications", "APPOINTMENTS"),
                },
              ].map((section, index) => (
                <motion.div
                  key={section.label}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                >
                  <div
                    className={`h-3 rounded-lg mb-2 ${
                      darkMode ? "bg-gray-600" : "bg-gray-300"
                    } w-32`}
                  />
                  <div
                    className={`p-3 rounded-xl border-l-4 max-sm:p-2 ${
                      darkMode
                        ? `bg-${section.color}-500/10 border-${section.color}-400`
                        : `bg-${section.color}-100/50 border-${section.color}-300`
                    }`}
                  >
                    <motion.div
                      animate={{ width: ["70%", "85%", "70%"] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className={`h-4 rounded-lg ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Additional Information Skeleton - Desktop Only */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="max-sm:hidden"
          >
            <div className="flex items-center gap-2 mb-4">
              <div
                className={`w-5 h-5 rounded-full ${
                  darkMode ? "bg-purple-600" : "bg-purple-400"
                }`}
              />
              <motion.div
                animate={{ width: ["190px", "220px", "190px"] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`h-6 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
            </div>

            <div className="space-y-4">
              {[
                {
                  gradient: "from-purple-500/5 to-pink-500/5",
                  label: textTranslator("Emergency Contact", "APPOINTMENTS"),
                },
                {
                  gradient: "from-emerald-500/5 to-teal-500/5",
                  label: textTranslator("Medical Insurance", "APPOINTMENTS"),
                },
                {
                  gradient: "from-orange-500/5 to-amber-500/5",
                  label: textTranslator("Notes", "APPOINTMENTS"),
                },
              ].map((section, index) => (
                <motion.div
                  key={section.label}
                  animate={{ y: [0, -2, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                >
                  <div
                    className={`h-3 rounded-lg mb-2 ${
                      darkMode ? "bg-gray-600" : "bg-gray-300"
                    } w-40`}
                  />
                  <div
                    className={`p-3 rounded-xl bg-gradient-to-r ${
                      section.gradient
                    } ${
                      darkMode
                        ? "border border-gray-700"
                        : "border border-gray-200"
                    }`}
                  >
                    <motion.div
                      animate={{ opacity: [0.6, 1, 0.6] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className={`h-5 rounded-lg ${
                        darkMode ? "bg-gray-700" : "bg-gray-200"
                      } w-full`}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Mobile-only Quick Medical Info Skeleton */}
          <motion.section
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="sm:hidden"
          >
            <div className="flex items-center gap-2 mb-3">
              <div
                className={`w-4 h-4 rounded-full ${
                  darkMode ? "bg-red-600" : "bg-red-400"
                }`}
              />
              <motion.div
                animate={{ width: ["120px", "140px", "120px"] }}
                transition={{ duration: 2, repeat: Infinity }}
                className={`h-5 rounded-lg ${
                  darkMode ? "bg-gray-700" : "bg-gray-200"
                }`}
              />
            </div>

            <div className="space-y-2">
              {[1, 2].map((item, index) => (
                <motion.div
                  key={item}
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                  className="flex items-center justify-between p-2 rounded-lg bg-gradient-to-r from-red-500/5 to-pink-500/5"
                >
                  <div
                    className={`h-3 rounded-lg ${
                      darkMode ? "bg-gray-600" : "bg-gray-300"
                    } w-16`}
                  />
                  <motion.div
                    animate={{ width: ["40px", "50px", "40px"] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
                    className={`h-3 rounded-lg ${
                      darkMode ? "bg-gray-700" : "bg-gray-200"
                    }`}
                  />
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>
      </motion.div>
    </div>
  );
};


export default function CreateAppointment() {
  usePageAccess("create-appointment");
  const darkMode = useSelector((s) => s.settings.darkMode);
  const defaultLanguage = useSelector((s) => s.settings.defaultLanguage);
  const { requests } = useSelector((s) => s.api);
  const isCreating = requests[createAppointmentKey]?.loading;
  const dispatch = useDispatch();
  const user = useUser();

  const navigate = useNavigate();
  const { source, values, redirectTo } = useSelector(
    (state) => state.navigation
  );

  const [patientLoading, setPatientLoading] = useState(false);

  const colors = {
    background: darkMode ? "bg-gray-900" : "bg-gray-50",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    cardHover: darkMode ? "hover:border-gray-600" : "hover:border-gray-400",
    input: darkMode
      ? "bg-gray-700 border-gray-600 placeholder-gray-500"
      : "bg-white border-gray-300 placeholder-gray-400",
    subBg: darkMode ? "bg-gray-700" : "bg-gray-100",
    text: {
      primary: darkMode ? "text-white" : "text-gray-800",
      secondary: darkMode ? "text-gray-300" : "text-gray-600",
      muted: darkMode ? "text-gray-400" : "text-gray-500",
    },
    button: {
      primary: darkMode
        ? "bg-blue-600 text-white hover:bg-blue-500"
        : "bg-blue-500 text-white hover:bg-blue-600",
      secondary: darkMode
        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300",
      danger: "bg-gradient-to-br from-red-500 to-pink-600 text-white",
    },
  };

  const [option, setOption] = useState(null);
  const [step, setStep] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [appointmentReason, setAppointmentReason] = useState(
    textTranslator("General consultation", "APPOINTMENTS")
  );

  const [appointment, setAppointment] = useState({
    patient: null,
    appointmentReason: "",
    appointmentDate: values?.date || new Date().toISOString().split("T")[0],
    time: values?.time || new Date().toTimeString().split(" ")[0].slice(0, 5),
    condition: "",
    note: "",
    status: "scheduled",
  });

  useEffect(() => {
    if (values?.patientId) {
      setPatientLoading(true);
      setStep(2);
      setOption("existing");
      api
        .get(`patients/patient-id/${values?.patientId}`)
        .then((res) => {
          const patientData = res.data;
          setSelectedPatient(patientData);
          setAppointment((a) => ({ ...a, patient: patientData }));
          setPatientLoading(false);
        })
        .catch((err) => {
          // Handle error silently
          setPatientLoading(false);
        });
    }
  }, [values?.patientId]);

  const next = () => {
    if (step === 0 && !option) return;
    if (step === 1) {
      if (option === "existing" && !selectedPatient) return;
      if (option === "new") return;
    }
    if (step === 2) {
      if (!appointment.appointmentDate || !appointment.time) return;
    }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const handlePatientSelect = (p) => {
    setSelectedPatient(p);
    setAppointment((a) => ({ ...a, patient: p }));
    setStep(2);
  };

  const handleUseNewPatient = (p) => {
    if (p) {
      setAppointment((a) => ({ ...a, patient: p }));
      setSelectedPatient(p);
      setStep(2);
    } else {
      setAppointment((a) => ({ ...a, patient: null }));
      setSelectedPatient(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const patientObj = appointment.patient || selectedPatient;

    if (!patientObj) {
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("Missing patient", "APPOINTMENTS"),
          message: textTranslator(
            "Please select or create a patient.",
            "APPOINTMENTS"
          ),
        })
      );
      return;
    }

    if (!appointment.appointmentDate || !appointment.time) {
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("Missing schedule", "APPOINTMENTS"),
          message: textTranslator("Please set date and time.", "APPOINTMENTS"),
        })
      );
      return;
    }

    dispatch(apiRequest("POST", createAppointmentKey));

    try {
      const isNewPatient = !!patientObj._raw;

      const basePayload = {
        appointmentReason: appointmentReason,
        appointmentDate: appointment.appointmentDate,
        appointmentTime: appointment.time,
        condition: appointment.condition,
        note: appointment.note,
        status: appointment.status,
        extra: { createdFrom: "frontend" },
      };

      const payload = isNewPatient
        ? {
            ...basePayload,
            patient: patientObj._raw,
            isNewPatient: true,
          }
        : {
            ...basePayload,
            patientId: patientObj.id,
          };

      await api.post(`appointments/create-appointment/${user.id}`, payload);

      dispatch(
        apiSuccess(
          "POST",
          createAppointmentKey,
          textTranslator("Appointment created", "APPOINTMENTS")
        )
      );
      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Created", "APPOINTMENTS"),
          message: textTranslator(
            "Appointment created successfully.",
            "APPOINTMENTS"
          ),
        })
      );

      if (redirectTo) {
        setTimeout(() => {
          dispatch(
          redirectWithState(
            {},
            redirectTo // target URL
          )
        );
        }, 3000);
      }

      setOption(null);
      setStep(0);
      setSelectedPatient(null);
      setAppointmentReason(
        textTranslator("General consultation", "APPOINTMENTS")
      );
      setAppointment({
        patient: null,
        appointmentReason: "",
        appointmentDate: new Date().toISOString().split("T")[0],
        time: new Date().toTimeString().split(" ")[0].slice(0, 5),
        condition: "",
        note: "",
        status: "scheduled",
      });
    } catch (err) {
      // Handle error silently
      const message =
        err?.response?.data?.error ||
        err.message ||
        textTranslator("Failed to create appointment", "APPOINTMENTS");

      dispatch(apiFetchError("POST", createAppointmentKey, message));
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("Error", "APPOINTMENTS"),
          message,
        })
      );
    }
  };

  const handleDisableStepClick = (targetStep, currentStep) => {
    switch (targetStep) {
      case 1:
        if (currentStep === 0 && !option) return true;
        if (currentStep === 1) {
          if (option === "existing" && !selectedPatient) return true;
          if (option === "new" && !selectedPatient) return true;
        }
        return false;
      case 2:
        if (currentStep === 0 && !option) return true;
        if (currentStep === 1) {
          if (option === "existing" && !selectedPatient) return true;
          if (option === "new" && !selectedPatient) return true;
        }
        return false;
      case 3:
        if (currentStep === 0 && !option) return true;
        if (currentStep === 1) {
          if (option === "existing" && !selectedPatient) return true;
          if (option === "new" && !selectedPatient) return true;
        }
        return false;
      default:
        return false;
    }
  };

  const handleDisableNextClick = (currentStep) => {
    let disabled = false;
    let title = textTranslator("Next Step", "APPOINTMENTS");
    switch (currentStep) {
      case 0:
        if (!option) {
          disabled = true;
          title = textTranslator(
            "Please choose an option to proceed",
            "APPOINTMENTS"
          );
        }
        break;
      case 1:
        if (option === "existing" && !selectedPatient) {
          disabled = true;
          title = textTranslator(
            "Please select a patient to proceed",
            "APPOINTMENTS"
          );
        }
        if (option === "new" && !selectedPatient) {
          disabled = true;
          title = textTranslator(
            "Please create a patient to proceed",
            "APPOINTMENTS"
          );
        }
        break;
      case 2:
        if (!appointment.appointmentDate || !appointment.time) {
          disabled = true;
          title = textTranslator(
            "Please set date and time to proceed",
            "APPOINTMENTS"
          );
        }
        break;
    }
    return { disabled, title };
  };

  const handleOption = (opt) => {
    setOption(opt);
    setSelectedPatient(null);
    setStep(1);
    if (source) onClearForm();
  };

  const steps = [
    textTranslator("Choose type", "APPOINTMENTS"),
    textTranslator("Patient", "APPOINTMENTS"),
    textTranslator("Appointment", "APPOINTMENTS"),
    textTranslator("Review", "APPOINTMENTS"),
  ];

  const sidebarVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const onClearForm = () => {
    dispatch(clearNavigationState());
    setSelectedPatient(null);
    setAppointmentReason(
      textTranslator("General consultation", "APPOINTMENTS")
    );
    setAppointment({
      patient: null,
      appointmentReason: "",
      appointmentDate: new Date().toISOString().split("T")[0],
      time: new Date().toTimeString().split(" ")[0].slice(0, 5),
      condition: "",
      note: "",
      status: "scheduled",
    });
  };

  return (
    <div className={arClass("")}>
      <Breadcrumbs />
      {isCreating && (
        <SubmitLoading
          colors={colors}
          message={textTranslator(
            "Setting up the appointment details...",
            "APPOINTMENTS"
          )}
        />
      )}
      <div className={arClass("p-6 transition-colors duration-300 max-sm:p-4")}>
        <div
          className={arClass(
            `min-h-[80vh] rounded-3xl shadow-2xl overflow-hidden grid grid-cols-12 max-sm:flex max-sm:flex-col ${colors.card} border`
          )}
        >
          {/* Enhanced Sidebar */}
          <motion.aside
            className={arClass(
              `col-span-2 p-6 max-sm:p-2 transition-colors duration-300  ${colors.subBg}`
            )}
            initial="hidden"
            animate="visible"
            variants={sidebarVariants}
          >
            <motion.h2
              className={arClass(
                "text-2xl font-bold mb-8 bg-gradient-to-r max-sm:text-center max-sm:text-lg max-sm:mb-4"
              )}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Text>Create Appointment</Text>
            </motion.h2>
            <nav
              className={arClass(
                "space-y-6 max-sm:flex  max-sm:space-y-0 max-sm:justify-between"
              )}
            >
              {steps.map((label, i) => (
                <motion.button
                  key={label}
                  className={arClass(
                    "flex items-center disabled:cursor-not-allowed max-sm:flex-col max-sm:space-y-2 max-sm:space-x-0"
                  )}
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setStep(i)}
                  disabled={handleDisableStepClick(i, step)}
                  title={`${
                    handleDisableStepClick(i, step)
                      ? textTranslator(
                          "Complete previous steps first",
                          "APPOINTMENTS"
                        )
                      : `${textTranslator(
                          "Go to",
                          "APPOINTMENTS"
                        )} ${label} ${textTranslator("step", "APPOINTMENTS")}`
                  }`}
                >
                  <div
                    className={arClass(
                      `w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 max-sm:w-8 max-sm:h-8 max-sm:text-xs max-sm:rounded-md ${
                        i === step
                          ? colors.button.primary
                          : `${
                              darkMode
                                ? "bg-gray-800 text-gray-300"
                                : "bg-gray-200 text-gray-600"
                            }`
                      }`
                    )}
                  >
                    {i + 1}
                  </div>
                  <div
                    className={arClass(
                      `text-sm font-medium max-sm:text-xs ${
                        i === step ? colors.text.primary : colors.text.secondary
                      }`
                    )}
                  >
                    {label}
                  </div>
                </motion.button>
              ))}
            </nav>
          </motion.aside>

          {/* Main Content */}
          <main className={arClass("col-span-10 p-6 max-sm:p-4")}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className={arClass("h-full flex flex-col")}
              >
                <div
                  className={arClass("flex items-center justify-between mb-8")}
                >
                  <motion.h3
                    className={arClass("text-3xl font-bold max-sm:text-sm")}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {steps[step]}
                  </motion.h3>
                  <motion.div
                    className={arClass(
                      `text-sm px-4 py-2 rounded-full max-sm:text-xs ${
                        darkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-200 text-gray-600"
                      }`
                    )}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Text>Step</Text> {step + 1} <Text>of</Text> {steps.length}
                  </motion.div>
                </div>

                <div className={arClass("flex-1")}>
                  {/* Step 0: Choose Option */}
                  {step === 0 && (
                    <div
                      className={arClass(
                        "grid grid-cols-2 gap-8 max-sm:flex max-sm:flex-col"
                      )}
                    >
                      <motion.div
                        onClick={() => handleOption("new")}
                        className={arClass(
                          `p-8 rounded-2xl cursor-pointer border-2 transition-all duration-300 ${
                            option === "new"
                              ? `border-blue-400 ${
                                  darkMode ? "bg-blue-900/20" : "bg-blue-50"
                                } shadow-lg`
                              : `${
                                  darkMode
                                    ? "border-gray-700 bg-gray-800"
                                    : "border-gray-200 bg-white"
                                } hover:border-blue-300 hover:shadow-sm`
                          }`
                        )}
                        whileHover={{
                          scale: 1.03,
                          y: -4,
                          transition: { type: "spring", stiffness: 300 },
                        }}
                        whileTap={{ scale: 0.98 }}
                        animate={
                          option === "new"
                            ? {
                                scale: 1.02,
                                borderColor: darkMode ? "#60a5fa" : "#3b82f6",
                                boxShadow:
                                  "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
                              }
                            : {}
                        }
                      >
                        <motion.div
                          className={arClass("flex items-center gap-4 mb-4")}
                          whileHover={{ x: 5 }}
                        >
                          <motion.div
                            className={arClass(
                              `w-12 h-12 min-w-12 max-sm:w-10 max-sm:h-10 max-sm:min-w-10 rounded-xl flex items-center justify-center ${
                                option === "new"
                                  ? "bg-blue-500 text-white"
                                  : `${
                                      darkMode
                                        ? "bg-gray-700 text-gray-300"
                                        : "bg-gray-100 text-gray-600"
                                    }`
                              }`
                            )}
                            animate={
                              option === "new"
                                ? {
                                    rotate: [0, -5, 0],
                                    scale: [1, 1.1, 1],
                                  }
                                : {}
                            }
                            transition={{ duration: 0.5 }}
                          >
                            <PlusIcon className="w-8 h-8 min-w-8 max-sm:w-5 max-sm:h-5 max-sm:min-w-5" />
                          </motion.div>
                          <h4
                            className={arClass(
                              `font-bold text-lg max-sm:text-sm ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`
                            )}
                          >
                            <Text>Create appointment for new patient</Text>
                          </h4>
                        </motion.div>
                        <motion.p
                          className={arClass(
                            `max-sm:text-xs ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`
                          )}
                          initial={{ opacity: 0.8 }}
                          whileHover={{ opacity: 1 }}
                        >
                          <Text>
                            Fill the patient details first, then schedule the
                            appointment.
                          </Text>
                        </motion.p>

                        {/* Animated checkmark when selected */}
                        <AnimatePresence>
                          {option === "new" && (
                            <motion.div
                              className={arClass(
                                "flex items-center gap-2 mt-4 text-blue-600"
                              )}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              transition={{ delay: 0.2 }}
                            >
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200 }}
                              >
                                <CheckIcon className="w-5 h-5" />
                              </motion.div>
                              <span
                                className={arClass(
                                  `text-sm font-medium max-sm:text-xs ${
                                    darkMode ? "text-blue-400" : "text-blue-600"
                                  }`
                                )}
                              >
                                <Text>Selected</Text>
                              </span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      <motion.div
                        onClick={() => handleOption("existing")}
                        className={arClass(
                          `p-8 rounded-2xl cursor-pointer border-2 transition-all duration-300 ${
                            option === "existing"
                              ? `border-blue-400 ${
                                  darkMode ? "bg-blue-900/20" : "bg-blue-50"
                                } shadow-lg`
                              : `${
                                  darkMode
                                    ? "border-gray-700 bg-gray-800"
                                    : "border-gray-200 bg-white"
                                } hover:border-blue-300 hover:shadow-sm`
                          }`
                        )}
                        whileHover={{
                          scale: 1.03,
                          y: -4,
                          transition: { type: "spring", stiffness: 300 },
                        }}
                        whileTap={{ scale: 0.98 }}
                        animate={
                          option === "existing"
                            ? {
                                scale: 1.02,
                                borderColor: darkMode ? "#60a5fa" : "#3b82f6",
                                boxShadow:
                                  "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
                              }
                            : {}
                        }
                      >
                        <motion.div
                          className={arClass("flex items-center gap-4 mb-4")}
                          whileHover={{ x: 5 }}
                        >
                          <motion.div
                            className={arClass(
                              `w-12 h-12 min-w-12 max-sm:w-10 max-sm:h-10 max-sm:min-w-10 rounded-xl flex items-center justify-center ${
                                option === "existing"
                                  ? "bg-blue-500 text-white"
                                  : `${
                                      darkMode
                                        ? "bg-gray-700 text-gray-300"
                                        : "bg-gray-100 text-gray-600"
                                    }`
                              }`
                            )}
                            animate={
                              option === "existing"
                                ? {
                                    rotate: [0, 5, 0],
                                    scale: [1, 1.1, 1],
                                  }
                                : {}
                            }
                            transition={{ duration: 0.5 }}
                          >
                            <UsersIcon className="w-8 h-8 min-w-8 max-sm:w-5 max-sm:h-5 max-sm:min-w-5" />
                          </motion.div>
                          <h4
                            className={arClass(
                              `font-bold text-lg max-sm:text-sm ${
                                darkMode ? "text-white" : "text-gray-900"
                              }`
                            )}
                          >
                            <Text>Create appointment for existing patient</Text>
                          </h4>
                        </motion.div>
                        <motion.p
                          className={arClass(
                            ` max-sm:text-xs ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`
                          )}
                          initial={{ opacity: 0.8 }}
                          whileHover={{ opacity: 1 }}
                        >
                          <Text>
                            Select a patient from your list then schedule.
                          </Text>
                        </motion.p>

                        {/* Animated checkmark when selected */}
                        <AnimatePresence>
                          {option === "existing" && (
                            <motion.div
                              className={arClass(
                                "flex items-center gap-2 mt-4 text-blue-600"
                              )}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              transition={{ delay: 0.2 }}
                            >
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 200 }}
                              >
                                <CheckIcon className="w-5 h-5" />
                              </motion.div>
                              <span
                                className={arClass(
                                  `text-sm font-medium max-sm:text-xs ${
                                    darkMode ? "text-blue-400" : "text-blue-600"
                                  }`
                                )}
                              >
                                <Text>Selected</Text>
                              </span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </div>
                  )}

                  {/* Step 1: Patient Selection */}
                  {step === 1 && (
                    <div>
                      {source === "dashboard-calendar" && values?.patientId ? (
                        patientLoading || !selectedPatient ? (
                          <PatientViewSkeleton
                            colors={colors}
                            darkMode={darkMode}
                          />
                        ) : (
                          <PatinetView
                            patient={selectedPatient}
                            colors={colors}
                          />
                        )
                      ) : (
                        <div>
                          {option === "existing" && (
                            <PatientSelection
                              darkMode={darkMode}
                              colors={colors}
                              onPatientSelect={handlePatientSelect}
                              selectedPatient={selectedPatient}
                              patientId={values?.patientId}
                              calculateAge={(d) =>
                                d
                                  ? Math.max(
                                      0,
                                      new Date().getFullYear() -
                                        new Date(d).getFullYear()
                                    )
                                  : ""
                              }
                            />
                          )}

                          {option === "new" && (
                            <>
                              <div className="mb-6">
                                <p className="text-gray-600 dark:text-gray-400 text-lg max-sm:text-sm">
                                  <Text>
                                    Fill patient details to create patient for
                                    this appointment.
                                  </Text>
                                </p>
                              </div>
                              <NewPatient
                                colors={colors}
                                setShowNewPatientModal={setShowNewPatientModal}
                                onPatientSelect={handleUseNewPatient}
                                selectPatient={selectedPatient}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Appointment Details */}
                  {step === 2 && (
                    <div
                      className={arClass(
                        "grid grid-cols-1 gap-4 normal-form max-sm:gap-3"
                      )}
                    >
                      {source && patientLoading ? (
                        <PatientFromSourceSkeleton darkMode={darkMode} />
                      ) : (
                        <>
                          <div className={arClass("col-span-1")}>
                            {source === "dashboard-calendar" &&
                              selectedPatient && (
                                <div
                                  className={arClass(
                                    "relative overflow-hidden"
                                  )}
                                >
                                  {/* Animated background elements */}
                                  <div
                                    className={arClass(
                                      `relative border rounded-xl p-4 mb-6 backdrop-blur-sm max-sm:p-3 max-sm:mb-4 ${
                                        darkMode
                                          ? "bg-gradient-to-br from-gray-800 via-gray-800 to-blue-900/10 border-blue-800/30"
                                          : "bg-gradient-to-br from-white via-white to-blue-50/30 border-blue-200/50"
                                      }`
                                    )}
                                  >
                                    {/* Header with cancel button */}
                                    <div
                                      className={arClass(
                                        "flex items-start justify-between mb-4 max-sm:mb-3"
                                      )}
                                    >
                                      <div
                                        className={arClass(
                                          "flex items-start max-sm:flex-col gap-2"
                                        )}
                                      >
                                        <div
                                          className={arClass(
                                            "relative flex-shrink-0"
                                          )}
                                        >
                                          <div
                                            className={arClass(
                                              `absolute inset-0 rounded-lg blur-lg animate-pulse ${
                                                darkMode
                                                  ? "bg-blue-700/30"
                                                  : "bg-blue-500/20"
                                              }`
                                            )}
                                          ></div>
                                          <div
                                            className={arClass(
                                              `relative flex-shrink-0 p-2 rounded-lg shadow ${
                                                darkMode
                                                  ? "bg-gradient-to-br from-blue-600 to-indigo-700"
                                                  : "bg-gradient-to-br from-blue-500 to-indigo-600"
                                              }`
                                            )}
                                          >
                                            <CalendarIcon className="w-8 h-8 text-white max-sm:w-6 max-sm:h-6" />
                                          </div>
                                        </div>

                                        <div className="ml-3 flex-1 max-sm:ml-0 max-sm:w-full">
                                          <div className="flex flex-col max-sm:gap-2">
                                            <div>
                                              <h3
                                                className={`text-lg font-bold max-sm:text-sm `}
                                              >
                                                <Text>
                                                  Finalizing Calendar Booking
                                                </Text>
                                              </h3>
                                              <p
                                                className={`mt-1 text-xs max-sm:text-xsm ${
                                                  darkMode
                                                    ? "text-gray-400"
                                                    : "text-gray-600"
                                                }`}
                                              >
                                                <Text>
                                                  Complete the appointment for
                                                  your newly created patient
                                                </Text>
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Cancel button */}
                                      <button
                                        onClick={() => {
                                          if (redirectTo) {
                                            navigate(redirectTo);
                                          } else {
                                            navigate("/dashboard");
                                          }
                                        }}
                                        className={`flex-shrink-0 ml-2 p-1.5 rounded-md transition-colors duration-200 max-sm:ml-0 ${
                                          darkMode
                                            ? "text-gray-500 hover:text-gray-300 hover:bg-gray-800"
                                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                        }`}
                                        title={textTranslator(
                                          "Cancel and return to dashboard",
                                          "APPOINTMENTS"
                                        )}
                                      >
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"
                                          />
                                        </svg>
                                      </button>
                                    </div>

                                    {/* Status badges row */}
                                    <div
                                      className={arClass(
                                        "flex flex-wrap items-center gap-2 mb-4"
                                      )}
                                    >
                                      <span
                                        className={arClass(
                                          `inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border max-sm:text-xsm max-sm:px-2 max-sm:py-1 ${
                                            darkMode
                                              ? "bg-gradient-to-r from-blue-900/30 to-indigo-900/30 text-blue-300 border-blue-700/30"
                                              : "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 border-blue-200/50"
                                          }`
                                        )}
                                      >
                                        <div
                                          className={arClass(
                                            `w-1.5 h-1.5 rounded-full mr-1.5 ${
                                              darkMode
                                                ? "bg-blue-400"
                                                : "bg-blue-500"
                                            }`
                                          )}
                                        ></div>
                                        <Text>Quick Schedule</Text>
                                      </span>

                                      {/* Dropdown for alternative actions */}
                                      <div
                                        className={arClass("relative group")}
                                      >
                                        <button
                                          className={arClass(
                                            `inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-200 max-sm:text-xsm max-sm:px-2 ${
                                              darkMode
                                                ? "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700 hover:border-gray-600"
                                                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                            }`
                                          )}
                                        >
                                          <span className="max-sm:text-xs">
                                            <Text>Actions</Text>
                                          </span>
                                          <svg
                                            className={`w-3 h-3 mx-1.5 ${
                                              darkMode
                                                ? "text-gray-400"
                                                : "text-gray-500"
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M19 9l-7 7-7-7"
                                            />
                                          </svg>
                                        </button>

                                        {/* Dropdown menu */}
                                        <div
                                          className={arClass(
                                            `absolute left-0 mt-1 w-48 rounded-lg shadow-lg border z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform translate-y-2 group-hover:translate-y-0 ${
                                              darkMode
                                                ? "bg-gray-800 border-gray-700"
                                                : "bg-white border-gray-200"
                                            }`
                                          )}
                                        >
                                          <div className={arClass("py-1")}>
                                            <div
                                              className={arClass(
                                                `px-3 py-1.5 text-xs font-medium uppercase tracking-wider ${
                                                  darkMode
                                                    ? "text-gray-400"
                                                    : "text-gray-500"
                                                }`
                                              )}
                                            >
                                              <Text>Change Patient</Text>
                                            </div>

                                            <button
                                              onClick={() => {
                                                setOption("existing");
                                                setStep(1);
                                                if (onClearForm) onClearForm();
                                              }}
                                              className={arClass(
                                                `w-full px-3 py-2 text-xs transition-colors duration-150 flex items-center group/item ${
                                                  darkMode
                                                    ? "text-gray-300 hover:bg-blue-900/30 hover:text-blue-300"
                                                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                                }`
                                              )}
                                            >
                                              <div
                                                className={arClass(
                                                  `flex-shrink-0 w-6 h-6 rounded flex items-start justify-center mr-2 group-hover/item:${
                                                    darkMode
                                                      ? "bg-blue-900/50"
                                                      : "bg-blue-100"
                                                  }`
                                                )}
                                              >
                                                <svg
                                                  className={arClass(
                                                    `w-3 h-3 group-hover/item:${
                                                      darkMode
                                                        ? "text-blue-400"
                                                        : "text-blue-600"
                                                    }`
                                                  )}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-8.5a6 6 0 01-6 6"
                                                  />
                                                </svg>
                                              </div>
                                              <div
                                                className={arClass(
                                                  "flex flex-col gap-1 items-start"
                                                )}
                                              >
                                                <div
                                                  className={arClass(
                                                    "font-medium"
                                                  )}
                                                >
                                                  <Text>Different Patient</Text>
                                                </div>
                                                <div
                                                  className={arClass(
                                                    `text-xsm mt-0.5 ${
                                                      darkMode
                                                        ? "text-gray-500"
                                                        : "text-gray-500"
                                                    }`
                                                  )}
                                                >
                                                  <Text>
                                                    Choose from existing
                                                  </Text>
                                                </div>
                                              </div>
                                            </button>

                                            <button
                                              onClick={() => {
                                                setOption("new");
                                                setStep(1);
                                                if (onClearForm) onClearForm();
                                              }}
                                              className={arClass(
                                                `w-full px-3 py-2 text-xs transition-colors duration-150 flex items-center group/item ${
                                                  darkMode
                                                    ? "text-gray-300 hover:bg-blue-900/30 hover:text-blue-300"
                                                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                                }`
                                              )}
                                            >
                                              <div
                                                className={arClass(
                                                  `flex-shrink-0 w-6 h-6 rounded flex items-start justify-center mr-2 group-hover/item:${
                                                    darkMode
                                                      ? "bg-blue-900/50"
                                                      : "bg-blue-100"
                                                  }`
                                                )}
                                              >
                                                <svg
                                                  className={arClass(
                                                    `w-3 h-3 group-hover/item:${
                                                      darkMode
                                                        ? "text-blue-400"
                                                        : "text-blue-600"
                                                    }`
                                                  )}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                                                  />
                                                </svg>
                                              </div>
                                              <div
                                                className={arClass(
                                                  "flex flex-col gap-1 items-start"
                                                )}
                                              >
                                                <div
                                                  className={arClass(
                                                    "font-medium"
                                                  )}
                                                >
                                                  <Text>New Patient</Text>
                                                </div>
                                                <div
                                                  className={arClass(
                                                    `text-xsm mt-0.5 ${
                                                      darkMode
                                                        ? "text-gray-500"
                                                        : "text-gray-500"
                                                    }`
                                                  )}
                                                >
                                                  <Text>Start fresh</Text>
                                                </div>
                                              </div>
                                            </button>

                                            <div
                                              className={`border-t my-1 ${
                                                darkMode
                                                  ? "border-gray-700"
                                                  : "border-gray-100"
                                              }`}
                                            ></div>

                                            <button
                                              onClick={() => {
                                                navigate(
                                                  `/patients/${selectedPatient.id}`
                                                );
                                              }}
                                              className={arClass(
                                                `w-full px-3 py-2 text-xs transition-colors duration-150 flex items-center group/item ${
                                                  darkMode
                                                    ? "text-gray-300 hover:bg-blue-900/30 hover:text-blue-300"
                                                    : "text-gray-700 hover:bg-blue-50 hover:text-blue-700"
                                                }`
                                              )}
                                            >
                                              <div
                                                className={arClass(
                                                  `flex-shrink-0 w-6 h-6 rounded flex items-start justify-center mr-2 group-hover/item:${
                                                    darkMode
                                                      ? "bg-blue-900/50"
                                                      : "bg-blue-100"
                                                  }`
                                                )}
                                              >
                                                <svg
                                                  className={arClass(
                                                    `w-3 h-3 group-hover/item:${
                                                      darkMode
                                                        ? "text-blue-400"
                                                        : "text-blue-600"
                                                    }`
                                                  )}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                  />
                                                </svg>
                                              </div>
                                              <div
                                                className={arClass(
                                                  "flex flex-col gap-1 items-start"
                                                )}
                                              >
                                                <div
                                                  className={arClass(
                                                    "font-medium"
                                                  )}
                                                >
                                                  <Text>Edit Patient</Text>
                                                </div>
                                                <div
                                                  className={arClass(
                                                    `text-xsm mt-0.5 ${
                                                      darkMode
                                                        ? "text-gray-500"
                                                        : "text-gray-500"
                                                    }`
                                                  )}
                                                >
                                                  <Text>
                                                    Update information
                                                  </Text>
                                                </div>
                                              </div>
                                            </button>

                                            <div
                                              className={arClass(
                                                `border-t my-1 ${
                                                  darkMode
                                                    ? "border-gray-700"
                                                    : "border-gray-100"
                                                }`
                                              )}
                                            ></div>

                                            <button
                                              onClick={() => {
                                                if (redirectTo) {
                                                  navigate(redirectTo);
                                                } else {
                                                  navigate("/dashboard");
                                                }
                                              }}
                                              className={arClass(
                                                `w-full px-3 py-2 text-xs transition-colors duration-150 flex items-center group/item ${
                                                  darkMode
                                                    ? "text-red-400 hover:bg-red-900/30"
                                                    : "text-red-600 hover:bg-red-50"
                                                }`
                                              )}
                                            >
                                              <div
                                                className={arClass(
                                                  `flex-shrink-0 w-6 h-6 rounded flex items-start justify-center mr-2 group-hover/item:${
                                                    darkMode
                                                      ? "bg-red-900/50"
                                                      : "bg-red-100"
                                                  }`
                                                )}
                                              >
                                                <svg
                                                  className={arClass(
                                                    `w-3 h-3 ${
                                                      darkMode
                                                        ? "text-red-400"
                                                        : "text-red-600"
                                                    }`
                                                  )}
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                  />
                                                </svg>
                                              </div>
                                              <div
                                                className={arClass(
                                                  "flex flex-col gap-1 items-start"
                                                )}
                                              >
                                                <div
                                                  className={arClass(
                                                    "font-medium"
                                                  )}
                                                >
                                                  <Text>Cancel & Exit</Text>
                                                </div>
                                                <div
                                                  className={arClass(
                                                    `text-xsm mt-0.5 ${
                                                      darkMode
                                                        ? "text-red-500/80"
                                                        : "text-red-500"
                                                    }`
                                                  )}
                                                >
                                                  <Text>
                                                    Return to dashboard
                                                  </Text>
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Patient card with hover effect */}
                                    <div
                                      className={`border rounded-xl p-4 mb-4 shadow-inner max-sm:p-3 ${
                                        darkMode
                                          ? "bg-gradient-to-br from-gray-800 via-blue-900/10 to-gray-800 border-blue-800/30"
                                          : "bg-gradient-to-br from-white via-blue-50/30 to-white border-blue-200/50"
                                      }`}
                                    >
                                      {/* Header Row - Compact on mobile */}
                                      <div
                                        className={arClass(
                                          "flex items-start justify-between mb-3 max-sm:mb-2.5"
                                        )}
                                      >
                                        <div
                                          className={arClass(
                                            "flex items-start flex-1 min-w-0 gap-1"
                                          )}
                                        >
                                          {/* Avatar with glow effect */}
                                          <div
                                            className={arClass(
                                              "relative flex-shrink-0"
                                            )}
                                          >
                                            <div
                                              className={arClass(
                                                "absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full blur opacity-30 max-sm:opacity-25"
                                              )}
                                            ></div>
                                            <div
                                              className={arClass(
                                                "relative w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow max-sm:w-9 max-sm:h-9"
                                              )}
                                            >
                                              <span
                                                className={arClass(
                                                  "text-white font-bold text-sm max-sm:text-xs flex gap-0.5"
                                                )}
                                              >
                                                {selectedPatient.firstName?.charAt(
                                                  0
                                                )}
                                                {selectedPatient.lastName?.charAt(
                                                  0
                                                )}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Patient Info - Better truncation on mobile */}
                                          <div
                                            className={arClass(
                                              "ml-3 flex-1 min-w-0 max-sm:ml-2.5"
                                            )}
                                          >
                                            <div
                                              className={arClass(
                                                "flex flex-col gap-1"
                                              )}
                                            >
                                              <h4
                                                className={arClass(
                                                  `font-bold text-base max-sm:text-sm truncate ${
                                                    darkMode
                                                      ? "text-gray-100"
                                                      : "text-gray-900"
                                                  }`
                                                )}
                                                title={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
                                              >
                                                {selectedPatient.firstName}{" "}
                                                {selectedPatient.lastName}
                                              </h4>

                                              {/* Status badges - Stack on mobile if needed */}
                                              <div
                                                className={arClass(
                                                  "flex flex-wrap items-center gap-1.5 mt-1 max-sm:mt-0.5"
                                                )}
                                              >
                                                <span
                                                  className={arClass(
                                                    `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap max-sm:px-1.5 max-sm:text-xsm ${
                                                      darkMode
                                                        ? "bg-green-900/30 text-green-300 border border-green-800/30"
                                                        : "bg-green-100 text-green-800 border border-green-200"
                                                    }`
                                                  )}
                                                >
                                                  <div
                                                    className={arClass(
                                                      `w-1.5 h-1.5 rounded-full mr-1 max-sm:w-1 max-sm:h-1 max-sm:mr-0.5 ${
                                                        darkMode
                                                          ? "bg-green-400"
                                                          : "bg-green-500"
                                                      }`
                                                    )}
                                                  ></div>
                                                  <Text>New Patient</Text>
                                                </span>
                                                <span
                                                  className={`text-xs whitespace-nowrap max-sm:text-xsm ${
                                                    darkMode
                                                      ? "text-gray-500"
                                                      : "text-gray-500"
                                                  }`}
                                                >
                                                  <Text>Created just now</Text>
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Quick edit button - Adjusted for mobile */}
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/patients/${selectedPatient.id}`
                                            )
                                          }
                                          className={`group relative flex-shrink-0 p-1.5 rounded-md transition-colors duration-200 ml-2 max-sm:mx-1 max-sm:p-1 ${
                                            darkMode
                                              ? "text-blue-400 hover:bg-blue-900/30"
                                              : "text-blue-600 hover:bg-blue-50"
                                          }`}
                                        >
                                          <svg
                                            className="w-4 h-4 max-sm:w-3.5 max-sm:h-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                            />
                                          </svg>

                                          {/* Tooltip span */}
                                          <span
                                            className={
                                              defaultLanguage === "ar"
                                                ? "absolute bottom-full left-1/2 transform -translate-x-1/5 mb-1 px-2 py-1 text-xs rounded bg-gray-800 text-white opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 whitespace-nowrap"
                                                : "absolute bottom-full left-1/2 transform -translate-x-[100%] mb-1 px-2 py-1 text-xs rounded bg-gray-800 text-white opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 whitespace-nowrap"
                                            }
                                          >
                                            {textTranslator(
                                              "Edit patient",
                                              "APPOINTMENTS"
                                            )}
                                          </span>
                                        </button>
                                      </div>

                                      {/* Patient Details - Better responsive grid */}
                                      <div
                                        className={arClass(
                                          "grid grid-cols-2 gap-2 max-sm:gap-1.5"
                                        )}
                                      >
                                        {/* Age */}
                                        <div
                                          className={arClass(
                                            `p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02] max-sm:p-2 ${
                                              darkMode
                                                ? "bg-gray-800/60 border-gray-700 hover:bg-gray-800/80 hover:border-blue-800/50"
                                                : "bg-white/90 border-gray-200/70 hover:bg-white hover:border-blue-200/80"
                                            }`
                                          )}
                                        >
                                          <div
                                            className={arClass(
                                              "flex items-start"
                                            )}
                                          >
                                            <div
                                              className={arClass(
                                                `p-1.5 rounded-md mr-2 max-sm:mr-1.5 max-sm:p-1 ${
                                                  darkMode
                                                    ? "bg-blue-900/30 text-blue-400"
                                                    : "bg-blue-100 text-blue-600"
                                                }`
                                              )}
                                            >
                                              <svg
                                                className="w-3.5 h-3.5 max-sm:w-3 max-sm:h-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z"
                                                />
                                              </svg>
                                            </div>
                                            <div className="min-w-0">
                                              <p
                                                className={`text-xs uppercase tracking-wider font-medium mb-0.5 max-sm:text-xsm ${
                                                  darkMode
                                                    ? "text-gray-400"
                                                    : "text-gray-500"
                                                }`}
                                              >
                                                <Text>Age</Text>
                                              </p>
                                              <p
                                                className={`font-semibold text-lg max-sm:text-sm ${
                                                  darkMode
                                                    ? "text-gray-100"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                {selectedPatient.age}
                                              </p>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Gender */}
                                        <div
                                          className={arClass(
                                            `p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02] max-sm:p-2 ${
                                              darkMode
                                                ? "bg-gray-800/60 border-gray-700 hover:bg-gray-800/80 hover:border-pink-800/50"
                                                : "bg-white/90 border-gray-200/70 hover:bg-white hover:border-pink-200/80"
                                            }`
                                          )}
                                        >
                                          <div
                                            className={arClass(
                                              "flex items-start"
                                            )}
                                          >
                                            <div
                                              className={arClass(
                                                `p-1.5 rounded-md mr-2 max-sm:mr-1.5 max-sm:p-1 ${
                                                  darkMode
                                                    ? "bg-pink-900/30 text-pink-400"
                                                    : "bg-pink-100 text-pink-600"
                                                }`
                                              )}
                                            >
                                              <svg
                                                className="w-3.5 h-3.5 max-sm:w-3 max-sm:h-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                />
                                              </svg>
                                            </div>
                                            <div className={arClass("min-w-0")}>
                                              <p
                                                className={arClass(
                                                  `text-xs uppercase tracking-wider font-medium mb-0.5 max-sm:text-xsm ${
                                                    darkMode
                                                      ? "text-gray-400"
                                                      : "text-gray-500"
                                                  }`
                                                )}
                                              >
                                                <Text>Gender</Text>
                                              </p>
                                              <p
                                                className={arClass(
                                                  `font-semibold text-lg max-sm:text-sm ${
                                                    darkMode
                                                      ? "text-gray-100"
                                                      : "text-gray-900"
                                                  }`
                                                )}
                                              >
                                                {selectedPatient.gender ? (
                                                  <span
                                                    className={
                                                      darkMode
                                                        ? selectedPatient.gender.toLowerCase() ===
                                                          "male"
                                                          ? "text-blue-400"
                                                          : selectedPatient.gender.toLowerCase() ===
                                                            "female"
                                                          ? "text-pink-400"
                                                          : "text-gray-300"
                                                        : selectedPatient.gender.toLowerCase() ===
                                                          "male"
                                                        ? "text-blue-600"
                                                        : selectedPatient.gender.toLowerCase() ===
                                                          "female"
                                                        ? "text-pink-600"
                                                        : "text-gray-700"
                                                    }
                                                  >
                                                    <Text page="PATIENTS">
                                                      {selectedPatient.gender}
                                                    </Text>
                                                  </span>
                                                ) : (
                                                  <span
                                                    className={
                                                      darkMode
                                                        ? "text-gray-500"
                                                        : "text-gray-400"
                                                    }
                                                  >
                                                    -
                                                  </span>
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Additional info row - Optional extra info */}
                                      {(selectedPatient.phone ||
                                        selectedPatient.email) && (
                                        <div
                                          className={arClass(
                                            "mt-3 max-sm:mt-2"
                                          )}
                                        >
                                          <div
                                            className={arClass(
                                              "flex flex-wrap gap-2 max-sm:gap-1.5"
                                            )}
                                          >
                                            {selectedPatient.phone && (
                                              <div className="flex items-center">
                                                <div
                                                  className={`p-1 rounded-md mr-1.5 max-sm:p-0.5 max-sm:mr-1 ${
                                                    darkMode
                                                      ? "bg-gray-700 text-gray-400"
                                                      : "bg-gray-100 text-gray-600"
                                                  }`}
                                                >
                                                  <svg
                                                    className="w-3 h-3 max-sm:w-2.5 max-sm:h-2.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth="2"
                                                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                                    />
                                                  </svg>
                                                </div>
                                                <span
                                                  className={`text-xs max-sm:text-xsm ${
                                                    darkMode
                                                      ? "text-gray-400"
                                                      : "text-gray-600"
                                                  }`}
                                                >
                                                  {selectedPatient.phone}
                                                </span>
                                              </div>
                                            )}

                                            {selectedPatient.email && (
                                              <div className="flex items-center">
                                                <div
                                                  className={`p-1 rounded-md mr-1.5 max-sm:p-0.5 max-sm:mr-1 ${
                                                    darkMode
                                                      ? "bg-gray-700 text-gray-400"
                                                      : "bg-gray-100 text-gray-600"
                                                  }`}
                                                >
                                                  <svg
                                                    className="w-3 h-3 max-sm:w-2.5 max-sm:h-2.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth="2"
                                                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                                    />
                                                  </svg>
                                                </div>
                                                <span
                                                  className={`text-xs truncate max-w-[120px] max-sm:text-xs max-sm:max-w-[100px] ${
                                                    darkMode
                                                      ? "text-gray-400"
                                                      : "text-gray-600"
                                                  }`}
                                                  title={selectedPatient.email}
                                                >
                                                  {selectedPatient.email}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    {/* Call to action */}
                                    <div
                                      className={arClass(
                                        `border rounded-lg p-3 ${
                                          darkMode
                                            ? "bg-gradient-to-r from-blue-900/20 to-indigo-900/20 border-blue-800/30"
                                            : "bg-gradient-to-r from-blue-50/80 to-indigo-50/60 border-blue-200/50"
                                        }`
                                      )}
                                    >
                                      <div
                                        className={arClass(
                                          "flex items-start justify-between max-sm:flex-col max-sm:gap-2"
                                        )}
                                      >
                                        <div
                                          className={arClass(
                                            "flex items-center max-sm:w-full"
                                          )}
                                        >
                                          <div
                                            className={arClass(
                                              "flex-shrink-0 mr-2"
                                            )}
                                          >
                                            <div
                                              className={arClass(
                                                `w-8 h-8 rounded-full flex items-center justify-center max-sm:w-6 max-sm:h-6 ${
                                                  darkMode
                                                    ? "bg-blue-900/30"
                                                    : "bg-blue-100"
                                                }`
                                              )}
                                            >
                                              <svg
                                                className={arClass(
                                                  `w-4 h-4 max-sm:w-3 max-sm:h-3 ${
                                                    darkMode
                                                      ? "text-blue-400"
                                                      : "text-blue-600"
                                                  }`
                                                )}
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            </div>
                                          </div>
                                          <div
                                            className={arClass("max-sm:flex-1")}
                                          >
                                            <p
                                              className={arClass(
                                                `font-medium text-sm max-sm:text-xs ${
                                                  darkMode
                                                    ? "text-gray-100"
                                                    : "text-gray-900"
                                                }`
                                              )}
                                            >
                                              <Text>
                                                Almost done! Complete the
                                                appointment details
                                              </Text>
                                            </p>
                                          </div>
                                        </div>

                                        {/* Quick cancel button */}
                                        <button
                                          onClick={() => {
                                            if (redirectTo) {
                                              navigate(redirectTo);
                                            } else {
                                              navigate("/dashboard");
                                            }
                                          }}
                                          className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 border max-sm:w-full max-sm:text-center max-sm:mt-1 ${
                                            darkMode
                                              ? "text-gray-400 hover:text-gray-300 hover:bg-gray-800 border-gray-700"
                                              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100 border-gray-200"
                                          }`}
                                        >
                                          <Text>Cancel</Text>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>

                          {/* Form Fields with Dark Mode - Responsive Grid */}
                          <div
                            className={arClass(
                              "grid grid-cols-1 gap-3 max-sm:gap-2"
                            )}
                          >
                            <div
                              className={arClass("flex flex-col w-full gap-2")}
                            >
                              <label
                                className={`text-xs font-medium  ${
                                  darkMode ? "text-gray-300" : "text-slate-700"
                                }`}
                              >
                                <Text>Reason</Text>
                              </label>
                              <CustomSelect
                                options={appointmentReasons.map((type) => ({
                                  value: type.nameEn,
                                  label:
                                    defaultLanguage === "ar"
                                      ? type.nameAr
                                      : defaultLanguage === "fr"
                                      ? type.nameFr
                                      : type.nameEn,
                                }))}
                                value={appointmentReason}
                                onChange={(e) =>
                                  setAppointmentReason(e.target.value)
                                }
                                placeholder={textTranslator(
                                  "Select visit type...",
                                  "APPOINTMENTS"
                                )}
                                allowCreate={true}
                                darkMode={darkMode}
                              />
                              {!appointmentReason && (
                                <p
                                  className={`text-xs mt-1 ${
                                    darkMode ? "text-red-400" : "text-red-500"
                                  }`}
                                >
                                  <Text>Please select a visit type.</Text>
                                </p>
                              )}
                            </div>

                            <div
                              className={arClass("flex flex-col w-full gap-2")}
                            >
                              <label
                                className={`text-xs font-medium  ${
                                  darkMode ? "text-gray-300" : "text-slate-700"
                                }`}
                              >
                                <Text>Condition</Text>
                              </label>
                              <input
                                value={appointment.condition}
                                onChange={(e) =>
                                  setAppointment((a) => ({
                                    ...a,
                                    condition: e.target.value,
                                  }))
                                }
                                className={arClass(
                                  `rounded-md px-3 py-3 text-sm focus:ring-2 focus:border-transparent transition-all duration-200 !border max-sm:text-xs max-sm:p-2 ${
                                    darkMode
                                      ? "!border-gray-600 focus:ring-blue-500 bg-gray-800 text-gray-100 placeholder-gray-500"
                                      : "!border-gray-200 focus:ring-blue-500"
                                  }`
                                )}
                                placeholder={textTranslator(
                                  "e.g. Follow-up, New issue",
                                  "APPOINTMENTS"
                                )}
                              />
                            </div>

                            <div
                              className={arClass(
                                "grid grid-cols-2 gap-3 max-sm:grid-cols-1"
                              )}
                            >
                              <div
                                className={arClass(
                                  "flex flex-col w-full gap-2"
                                )}
                              >
                                <label
                                  className={`text-xs font-medium  ${
                                    darkMode
                                      ? "text-gray-300"
                                      : "text-slate-700"
                                  }`}
                                >
                                  <Text>Date</Text>
                                  {values?.date && (
                                    <small
                                      className={`mx-1 ${
                                        darkMode
                                          ? "text-red-400"
                                          : "text-red-300"
                                      }`}
                                    >
                                      (
                                      {textTranslator(
                                        "pre-filled",
                                        "APPOINTMENTS"
                                      )}
                                      )
                                    </small>
                                  )}
                                </label>

                                <DateInput
                                  value={appointment.appointmentDate}
                                  onChange={(e) => {
                                    const val =
                                      e && e.target ? e.target.value : e;
                                    setAppointment((a) => ({
                                      ...a,
                                      appointmentDate: val,
                                    }));
                                  }}
                                  className={arClass("w-full sm:w-auto")}
                                />
                              </div>

                              <div
                                className={arClass(
                                  "flex flex-col w-full gap-2"
                                )}
                              >
                                <label
                                  className={`text-xs font-medium  ${
                                    darkMode
                                      ? "text-gray-300"
                                      : "text-slate-700"
                                  }`}
                                >
                                  <Text>Time</Text>
                                  {values?.time && (
                                    <small
                                      className={`mx-1 ${
                                        darkMode
                                          ? "text-red-400"
                                          : "text-red-300"
                                      }`}
                                    >
                                      (
                                      {textTranslator(
                                        "pre-filled",
                                        "APPOINTMENTS"
                                      )}
                                      )
                                    </small>
                                  )}
                                </label>
                                <TimeInput
                                  value={appointment.time}
                                  onChange={(e) => {
                                    const val =
                                      e && e.target ? e.target.value : e;
                                    setAppointment((a) => ({
                                      ...a,
                                      time: val,
                                    }));
                                  }}
                                  className={arClass("w-full sm:w-auto")}
                                />
                              </div>
                            </div>

                            <div
                              className={arClass("flex flex-col w-full gap-2")}
                            >
                              <label
                                className={`text-xs font-medium  ${
                                  darkMode ? "text-gray-300" : "text-slate-700"
                                }`}
                              >
                                <Text>Status</Text>
                              </label>

                              <CustomSelect
                                options={[
                                  {
                                    value: "scheduled",
                                    label: textTranslator(
                                      "Scheduled",
                                      "APPOINTMENTS"
                                    ),
                                  },
                                  {
                                    value: "completed",
                                    label: textTranslator(
                                      "Completed",
                                      "APPOINTMENTS"
                                    ),
                                  },
                                  {
                                    value: "cancelled",
                                    label: textTranslator(
                                      "Cancelled",
                                      "APPOINTMENTS"
                                    ),
                                  },
                                  {
                                    value: "no-show",
                                    label: textTranslator(
                                      "No-Show",
                                      "APPOINTMENTS"
                                    ),
                                  },
                                  {
                                    value: "in-progress",
                                    label: textTranslator(
                                      "In-Progress",
                                      "APPOINTMENTS"
                                    ),
                                  },
                                ]}
                                value={appointment.status}
                                onChange={(e) => {
                                  setAppointment((a) => ({
                                    ...a,
                                    status: e.target.value,
                                  }));
                                }}
                                placeholder={textTranslator(
                                  "Select appointment reason...",
                                  "APPOINTMENTS"
                                )}
                                allowCreate={false}
                                allowSearch={false}
                                className="max-sm:text-sm"
                              />
                            </div>

                            <div
                              className={arClass("flex flex-col w-full gap-2")}
                            >
                              <label
                                className={`text-xs font-medium  ${
                                  darkMode ? "text-gray-300" : "text-slate-700"
                                }`}
                              >
                                <Text>Note</Text>
                              </label>
                              <textarea
                                value={appointment.note}
                                onChange={(e) =>
                                  setAppointment((a) => ({
                                    ...a,
                                    note: e.target.value,
                                  }))
                                }
                                className={arClass(
                                  `rounded-md px-3 py-3 text-sm focus:ring-2 focus:border-transparent transition-all duration-200 !border max-sm:text-xs max-sm:p-2 ${
                                    darkMode
                                      ? "!border-gray-600 focus:ring-blue-500 bg-gray-800 text-gray-100 placeholder-gray-500"
                                      : "!border-gray-200 focus:ring-blue-500"
                                  }`
                                )}
                                placeholder={textTranslator(
                                  "notesPlaceholder",
                                  "APPOINTMENTS"
                                )}
                                rows={3}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {/* Step 3: Review */}
                  {step === 3 && (
                    <div className={arClass("space-y-6 max-sm:space-y-4")}>
                      {/* Patient Review Section */}
                      <div
                        className={arClass("rounded-2xl border p-6 max-sm:p-4")}
                      >
                        <div
                          className={arClass(
                            "flex items-center justify-between mb-4 max-sm:mb-3"
                          )}
                        >
                          <h4
                            className={arClass(
                              `text-lg font-bold max-sm:text-sm ${colors.text.primary}`
                            )}
                          >
                            <Text>Patient Details</Text>
                          </h4>
                          <motion.button
                            onClick={() => setStep(1)}
                            className={arClass(
                              `text-sm px-3 py-1.5 rounded-lg transition-colors max-sm:text-xs max-sm:px-2.5 max-sm:py-1 ${
                                darkMode
                                  ? "text-blue-400 hover:bg-blue-900/30"
                                  : "text-blue-600 hover:bg-blue-50"
                              }`
                            )}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Text>Edit</Text>
                          </motion.button>
                        </div>

                        {selectedPatient && (
                          <div
                            className={arClass(
                              "grid grid-cols-1 md:grid-cols-2 gap-4 max-sm:gap-3"
                            )}
                          >
                            {/* Patient Info Card */}
                            <div
                              className={arClass(
                                `p-4 rounded-xl max-sm:p-3 ${
                                  darkMode ? "bg-gray-800/50" : "bg-gray-50"
                                }`
                              )}
                            >
                              <div
                                className={arClass(
                                  "flex items-center mb-3 max-sm:mb-2"
                                )}
                              >
                                <div
                                  className={arClass(
                                    `w-12 h-12 rounded-full flex items-center justify-center mr-3 max-sm:w-10 max-sm:h-10 max-sm:mr-2 ${
                                      darkMode
                                        ? "bg-blue-900/30 text-blue-300"
                                        : "bg-blue-100 text-blue-700"
                                    }`
                                  )}
                                >
                                  <span className="font-bold text-lg max-sm:text-sm">
                                    {selectedPatient.firstName?.charAt(0)}
                                    {selectedPatient.lastName?.charAt(0)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5
                                    className={`font-bold truncate max-sm:text-sm ${colors.text.primary}`}
                                  >
                                    {selectedPatient.firstName}{" "}
                                    {selectedPatient.lastName}
                                  </h5>
                                  <p
                                    className={`text-sm max-sm:text-xs ${colors.text.secondary}`}
                                  >
                                    {option === "new"
                                      ? textTranslator(
                                          "New Patient",
                                          "APPOINTMENTS"
                                        )
                                      : textTranslator(
                                          "Existing Patient",
                                          "APPOINTMENTS"
                                        )}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 mt-3 max-sm:mt-2 max-sm:gap-1.5">
                                <div className="truncate">
                                  <p
                                    className={`text-xs max-sm:text-xs ${colors.text.muted}`}
                                  >
                                    <Text>Age</Text>
                                  </p>
                                  <p
                                    className={`font-medium truncate max-sm:text-sm ${colors.text.primary}`}
                                  >
                                    {selectedPatient.age}
                                  </p>
                                </div>
                                <div className="truncate">
                                  <p
                                    className={`text-xs max-sm:text-xs ${colors.text.muted}`}
                                  >
                                    <Text>Gender</Text>
                                  </p>
                                  <p
                                    className={`font-medium truncate max-sm:text-sm ${colors.text.primary}`}
                                  >
                                    <Text page="PATIENTS">
                                      {selectedPatient.gender}
                                    </Text>
                                  </p>
                                </div>
                                <div className="truncate">
                                  <p
                                    className={`text-xs max-sm:text-xs ${colors.text.muted}`}
                                  >
                                    <Text>Phone</Text>
                                  </p>
                                  <p
                                    className={`font-medium truncate max-sm:text-sm ${colors.text.primary}`}
                                  >
                                    {selectedPatient.phone ||
                                      textTranslator("—", "APPOINTMENTS")}
                                  </p>
                                </div>
                                <div className="truncate">
                                  <p
                                    className={`text-xs max-sm:text-xs ${colors.text.muted}`}
                                  >
                                    <Text>Email</Text>
                                  </p>
                                  <p
                                    className={`font-medium truncate max-sm:text-sm ${colors.text.primary}`}
                                  >
                                    {selectedPatient.email ||
                                      textTranslator("—", "APPOINTMENTS")}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Appointment Quick Stats */}
                            <div
                              className={arClass(
                                `p-4 rounded-xl max-sm:p-3 ${
                                  darkMode ? "bg-gray-800/50" : "bg-gray-50"
                                }`
                              )}
                            >
                              <h6
                                className={arClass(
                                  `font-semibold mb-3 max-sm:mb-2 max-sm:text-sm ${colors.text.primary}`
                                )}
                              >
                                <Text>Appointment Summary</Text>
                              </h6>
                              <div
                                className={arClass(
                                  "space-y-3 max-sm:space-y-2"
                                )}
                              >
                                <div
                                  className={arClass(
                                    "flex items-center justify-between"
                                  )}
                                >
                                  <span
                                    className={arClass(
                                      `text-sm max-sm:text-xs ${colors.text.secondary}`
                                    )}
                                  >
                                    <Text>Patient Type</Text>
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium max-sm:text-xs max-sm:px-1.5 ${
                                      darkMode
                                        ? "bg-blue-900/30 text-blue-300"
                                        : "bg-blue-100 text-blue-700"
                                    }`}
                                  >
                                    {option === "new"
                                      ? textTranslator(
                                          "New Patient",
                                          "APPOINTMENTS"
                                        )
                                      : textTranslator(
                                          "Existing Patient",
                                          "APPOINTMENTS"
                                        )}
                                  </span>
                                </div>
                                <div
                                  className={arClass(
                                    "flex items-center justify-between"
                                  )}
                                >
                                  <span
                                    className={arClass(
                                      `text-sm max-sm:text-xs ${colors.text.secondary}`
                                    )}
                                  >
                                    <Text>Created By</Text>
                                  </span>
                                  <span
                                    className={arClass(
                                      `font-medium truncate max-sm:text-sm ${colors.text.primary}`
                                    )}
                                  >{
                                    option === "new" ? (
                                      <>
                                      {user?.name} (
                                    <Text page="PATIENTS">
                                      {user?.role}
                                    </Text>
                                      )
                                      </>
                                    ) : 
                                      <>
                                      {selectedPatient.creator.firstName}{" "}
                                    {selectedPatient.creator.lastName} (
                                    <Text page="PATIENTS">
                                      {selectedPatient.creator.role}
                                    </Text>
                                    )
                                      </>
                                  }
                                    
                                  </span>
                                </div>
                                {
                                  source && (
                                    <div
                                  className={arClass(
                                    "flex items-center justify-between"
                                  )}
                                >
                                  <span
                                    className={arClass(
                                      `text-sm max-sm:text-xs ${colors.text.secondary}`
                                    )}
                                  >
                                    <Text>Source</Text>
                                  </span>
                                  <span
                                    className={`text-sm truncate max-sm:text-xs ${colors.text.primary}`}
                                  >
                                    <Text>
                                      {source ||
                                        textTranslator(
                                          "Manual Entry",
                                          "APPOINTMENTS"
                                        )}
                                    </Text>
                                  </span>
                                </div>
                                  )
                                }
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Appointment Details Section */}
                      <div
                        className={arClass("rounded-2xl border p-6 max-sm:p-4")}
                      >
                        <div
                          className={arClass(
                            "flex items-center justify-between mb-4 max-sm:mb-3"
                          )}
                        >
                          <h4
                            className={arClass(
                              `text-lg font-bold max-sm:text-sm ${colors.text.primary}`
                            )}
                          >
                            <Text>Appointment Details</Text>
                          </h4>
                          <motion.button
                            onClick={() => setStep(2)}
                            className={arClass(
                              `text-sm px-3 py-1.5 rounded-lg transition-colors max-sm:text-xs max-sm:px-2.5 max-sm:py-1 ${
                                darkMode
                                  ? "text-blue-400 hover:bg-blue-900/30"
                                  : "text-blue-600 hover:bg-blue-50"
                              }`
                            )}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Text>Edit</Text>
                          </motion.button>
                        </div>

                        <div
                          className={arClass(
                            "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-sm:gap-3"
                          )}
                        >
                          {/* Date & Time Card */}
                          <div
                            className={arClass(
                              `p-4 rounded-xl max-sm:p-3 ${
                                darkMode ? "bg-gray-800/50" : "bg-gray-50"
                              }`
                            )}
                          >
                            <h3
                              className={arClass(
                                `font-semibold mb-3 max-sm:mb-2 max-sm:text-sm ${colors.text.primary}`
                              )}
                            >
                              <Text>Schedule</Text>
                            </h3>
                            <div
                              className={arClass(
                                "flex items-center gap-3 max-sm:gap-2 mb-2"
                              )}
                            >
                              <div
                                className={arClass("flex items-center gap-1")}
                              >
                                <svg
                                  className={`w-5 h-5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                                <div
                                  className={arClass(
                                    "min-w-0 flex-1 flex gap-2 max-sm:gap-1 items-center"
                                  )}
                                >
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.muted}`
                                    )}
                                  >
                                    <Text>Date</Text>:
                                  </p>
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.primary}`
                                    )}
                                  >
                                    {formatDate(appointment.appointmentDate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className={arClass(
                                "flex items-center gap-3 max-sm:gap-2"
                              )}
                            >
                              <div
                                className={arClass("flex items-center gap-1")}
                              >
                                <svg
                                  className={`w-5 h-5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <div
                                  className={arClass(
                                    "min-w-0 flex-1 flex gap-2 max-sm:gap-1 items-center"
                                  )}
                                >
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.muted}`
                                    )}
                                  >
                                    <Text>Time</Text>:
                                  </p>
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.primary}`
                                    )}
                                  >
                                    {formatTime(appointment.time)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Reason & Status Card */}
                          <div
                            className={arClass(
                              `p-4 rounded-xl max-sm:p-3 ${
                                darkMode ? "bg-gray-800/50" : "bg-gray-50"
                              }`
                            )}
                          >
                            <h3
                              className={arClass(
                                `font-semibold mb-3 max-sm:mb-2 max-sm:text-sm ${colors.text.primary}`
                              )}
                            >
                              <Text>Visit Information</Text>
                            </h3>
                            <div
                              className={arClass(
                                "flex items-center gap-3 max-sm:gap-2 mb-2"
                              )}
                            >
                              <div
                                className={arClass("flex items-center gap-1")}
                              >
                                <svg
                                  className={`w-5 h-5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <div
                                  className={arClass(
                                    "min-w-0 flex-1 flex gap-2 max-sm:gap-1 items-center"
                                  )}
                                >
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.muted}`
                                    )}
                                  >
                                    <Text>Reason</Text>:
                                  </p>
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.primary}`
                                    )}
                                  >
                                    {appointmentReason ||
                                      textTranslator(
                                        "General consultation",
                                        "APPOINTMENTS"
                                      )}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className={arClass(
                                "flex items-center gap-3 max-sm:gap-2 mb-2"
                              )}
                            >
                              <div
                                className={arClass("flex items-center gap-1")}
                              >
                                <svg
                                  className={`w-5 h-5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <div
                                  className={arClass(
                                    "min-w-0 flex-1 flex gap-2 max-sm:gap-1 items-center"
                                  )}
                                >
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.muted}`
                                    )}
                                  >
                                    <Text>Condition</Text>:
                                  </p>
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.primary}`
                                    )}
                                  >
                                    {appointment.condition ||
                                      textTranslator(
                                        "Not specified",
                                        "APPOINTMENTS"
                                      )}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div
                              className={arClass(
                                "flex items-center gap-3 max-sm:gap-2"
                              )}
                            >
                              <div
                                className={arClass("flex items-center gap-1")}
                              >
                                <svg
                                  className={`w-5 h-5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                  />
                                </svg>
                                <div
                                  className={arClass(
                                    "min-w-0 flex-1 flex gap-2 max-sm:gap-1 items-center"
                                  )}
                                >
                                  <p
                                    className={arClass(
                                      `font-medium truncate max-sm:text-xs ${colors.text.muted}`
                                    )}
                                  >
                                    <Text>Status</Text>:
                                  </p>
                                  <span
                                    className={arClass(
                                      `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium max-sm:text-xs max-sm:px-1.5 ${
                                        appointment.status === "scheduled"
                                          ? darkMode
                                            ? "bg-blue-900/30 text-blue-300"
                                            : "bg-blue-100 text-blue-700"
                                          : appointment.status === "completed"
                                          ? darkMode
                                            ? "bg-green-900/30 text-green-300"
                                            : "bg-green-100 text-green-700"
                                          : appointment.status === "cancelled"
                                          ? darkMode
                                            ? "bg-red-900/30 text-red-300"
                                            : "bg-red-100 text-red-700"
                                          : darkMode
                                          ? "bg-yellow-900/30 text-yellow-300"
                                          : "bg-yellow-100 text-yellow-700"
                                      }`
                                    )}
                                  >
                                    <Text>{appointment.status}</Text>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Notes Card */}
                          <div
                            className={arClass(
                              `p-4 rounded-xl max-sm:p-3 ${
                                darkMode ? "bg-gray-800/50" : "bg-gray-50"
                              }`
                            )}
                          >
                            <h3
                              className={arClass(
                                `font-semibold mb-3 max-sm:mb-2 max-sm:text-sm ${colors.text.primary}`
                              )}
                            >
                              <Text>Notes</Text>
                            </h3>
                            <div
                              className={arClass(
                                "flex items-start gap-3 max-sm:gap-2"
                              )}
                            >
                              <svg
                                className={`w-5 h-5 mt-0.5 mr-2 max-sm:w-4 max-sm:h-4 max-sm:mr-1.5 ${colors.text.muted}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              <div className={arClass("min-w-0 flex-1")}>
                                <p
                                  className={arClass(
                                    `font-medium truncate max-sm:text-xs ${colors.text.muted} mb-1`
                                  )}
                                >
                                  <Text>Notes</Text>:
                                </p>
                                <div
                                  className={arClass(
                                    "min-h-[80px] max-sm:min-h-[60px]"
                                  )}
                                >
                                  {appointment.note ? (
                                    <p
                                      className={arClass(
                                        `text-sm max-sm:text-xs ${colors.text.primary} whitespace-pre-wrap break-words`
                                      )}
                                    >
                                      {appointment.note}
                                    </p>
                                  ) : (
                                    <p
                                      className={arClass(
                                        `text-sm italic max-sm:text-xs ${colors.text.muted}`
                                      )}
                                    >
                                      <Text>No additional notes provided</Text>
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Summary Card */}
                      <div
                        className={arClass(
                          `rounded-2xl border p-6 max-sm:p-4 ${
                            darkMode
                              ? "bg-gradient-to-br from-blue-900/10 to-indigo-900/10 border-blue-800/30"
                              : "bg-gradient-to-br from-blue-50 to-indigo-50/30 border-blue-200"
                          }`
                        )}
                      >
                        <div
                          className={arClass(
                            "flex items-center mb-4 max-sm:mb-3"
                          )}
                        >
                          <div
                            className={arClass(
                              `w-10 h-10 rounded-full flex items-center justify-center mr-3 max-sm:w-8 max-sm:h-8 max-sm:mr-2 ${
                                darkMode
                                  ? "bg-blue-900/30 text-blue-300"
                                  : "bg-blue-100 text-blue-600"
                              }`
                            )}
                          >
                            <svg
                              className={arClass(
                                "w-5 h-5 max-sm:w-4 max-sm:h-4"
                              )}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className={arClass("min-w-0 flex-1")}>
                            <h4
                              className={arClass(
                                `font-bold truncate max-sm:text-sm ${colors.text.primary}`
                              )}
                            >
                              <Text>Ready to Create Appointment</Text>
                            </h4>
                            <p
                              className={arClass(
                                `text-sm truncate max-sm:text-xs ${colors.text.secondary}`
                              )}
                            >
                              <Text>Review all details before proceeding</Text>
                            </p>
                          </div>
                        </div>

                        <div
                          className={arClass(
                            "grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 max-sm:gap-2 max-sm:mt-3"
                          )}
                        >
                          <div className={arClass("text-center")}>
                            <div
                              className={arClass(
                                `text-2xl font-bold mb-1 max-sm:text-lg ${
                                  darkMode ? "text-blue-400" : "text-blue-600"
                                }`
                              )}
                            >
                              {option === "new" ? "1" : "2"}
                            </div>
                            <div
                              className={arClass(
                                `text-xs max-sm:text-xs ${colors.text.secondary}`
                              )}
                            >
                              {option === "new"
                                ? textTranslator("New Patient", "APPOINTMENTS")
                                : textTranslator(
                                    "Existing Patient",
                                    "APPOINTMENTS"
                                  )}
                            </div>
                          </div>
                          <div className={arClass("text-center")}>
                            <div
                              className={arClass(
                                `text-2xl font-bold mb-1 max-sm:text-lg ${
                                  darkMode ? "text-blue-400" : "text-blue-600"
                                }`
                              )}
                            >
                              {appointment.appointmentDate
                                ? "✓"
                                : textTranslator("—", "APPOINTMENTS")}
                            </div>
                            <div
                              className={arClass(
                                `text-xs max-sm:text-xs ${colors.text.secondary}`
                              )}
                            >
                              <Text>Date Set</Text>
                            </div>
                          </div>
                          <div className={arClass("text-center")}>
                            <div
                              className={arClass(
                                `text-2xl font-bold mb-1 max-sm:text-lg ${
                                  darkMode ? "text-blue-400" : "text-blue-600"
                                }`
                              )}
                            >
                              {appointment.time
                                ? "✓"
                                : textTranslator("—", "APPOINTMENTS")}
                            </div>
                            <div
                              className={arClass(
                                `text-xs max-sm:text-xs ${colors.text.secondary}`
                              )}
                            >
                              <Text>Time Set</Text>
                            </div>
                          </div>
                          <div className={arClass("text-center")}>
                            <div
                              className={arClass(
                                `text-2xl font-bold mb-1 max-sm:text-lg ${
                                  darkMode ? "text-blue-400" : "text-blue-600"
                                }`
                              )}
                            >
                              ✓
                            </div>
                            <div
                              className={arClass(
                                `text-xs max-sm:text-xs ${colors.text.secondary}`
                              )}
                            >
                              <Text>Ready to Submit</Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer Controls */}
                <div
                  className={arClass(
                    "mt-8 flex items-center justify-between pt-6  border-gray-200 "
                  )}
                >
                  <div className="flex gap-3">
                    <motion.button
                      disabled={step === 0}
                      onClick={back}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:py-2 ${
                        step === 0
                          ? `${
                              darkMode
                                ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`
                          : darkMode
                          ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                      whileHover={step !== 0 ? { scale: 1.02, y: -1 } : {}}
                      whileTap={step !== 0 ? { scale: 0.98, y: 0 } : {}}
                    >
                      <Text>Back</Text>
                    </motion.button>

                    {step !== steps.length - 1 && (
                      <motion.button
                        onClick={next}
                        disabled={handleDisableNextClick(step).disabled}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:py-2 ${
                          handleDisableNextClick(step).disabled
                            ? darkMode
                              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : colors.button.primary
                        }
                        ${step === 1 && option === "new" ? "hidden" : ""}
                        `}
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <Text>Next</Text>
                      </motion.button>
                    )}

                    {step === steps.length - 1 && (
                      <motion.button
                        onClick={handleSubmit}
                        className="px-8 py-3 rounded-xl font-semibold bg-green-600 
                         text-white shadow-lg hover:shadow-md transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:px-6 max-sm:py-2"
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <Text>Complete Appointment</Text>
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}
